import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Layout, Menu, Card, Row, Col, Table, Tag, Button, Typography, Avatar,
    message, Spin, Modal, Form, Input, Select, Dropdown, Badge, Drawer, Space
} from 'antd';
import {
    DashboardOutlined, TeamOutlined, UserOutlined, LogoutOutlined,
    DollarOutlined, CopyOutlined, BellOutlined, CalendarOutlined,
    MenuFoldOutlined, MenuUnfoldOutlined, UserAddOutlined, PhoneOutlined,
    LockOutlined, MailOutlined, EnvironmentOutlined, ArrowLeftOutlined,
    EyeOutlined, CheckCircleOutlined, ClockCircleOutlined, HourglassOutlined,
    ExclamationCircleOutlined, InfoCircleOutlined, MenuOutlined, CloseOutlined,
    HomeOutlined, SettingOutlined, WalletOutlined, CarOutlined, SearchOutlined,
    RiseOutlined, TrophyOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import { affiliateService, cityService } from '../../common/services/api';
import logoTigoon from '../../assets/logo-tigoon.png';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import './AffiliateDashboard.css';

// Configurer dayjs en français
dayjs.locale('fr');

const { Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { SubMenu } = Menu;
const { Option } = Select;

const AffiliateDashboardPage = () => {
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState('dashboard');
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);

    // Modals
    const [driverModalVisible, setDriverModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [driverForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [addingDriver, setAddingDriver] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [cities, setCities] = useState([]);

    // Revenue states
    const [commissionsData, setCommissionsData] = useState(null);
    const [driversProgress, setDriversProgress] = useState([]);
    const [commissionsHistory, setCommissionsHistory] = useState([]);
    const [loadingRevenues, setLoadingRevenues] = useState(false);
    const [selectedDriverProgress, setSelectedDriverProgress] = useState(null);
    const [driverSearchTerm, setDriverSearchTerm] = useState('');
    const [referralsSearchTerm, setReferralsSearchTerm] = useState('');

    // 130h revenues states
    const [revenues130h, setRevenues130h] = useState([]);
    const [revenues130hPeriodStats, setRevenues130hPeriodStats] = useState([]);
    const [hoursThreshold, setHoursThreshold] = useState(130);

    // Collected revenues states
    const [collectedRevenues, setCollectedRevenues] = useState([]);

    // Bonus recharge states
    const [bonusRecharges, setBonusRecharges] = useState([]);
    const [loadingBonusRecharge, setLoadingBonusRecharge] = useState(false);

    // Monthly retentions states
    const [monthlyRetentions, setMonthlyRetentions] = useState([]);
    const [monthlyRetentionsPeriodStats, setMonthlyRetentionsPeriodStats] = useState([]);
    const [loadingMonthlyRetentions, setLoadingMonthlyRetentions] = useState(false);

    // Commission rates (loaded from API, with defaults)
    const [commissionRates, setCommissionRates] = useState({
        accountCreation: 500,
        rides5: 900,
        rides15: 1350,
        rides35: 1800,
        rides55: 1800,
        hours130: 900,
        // Réactivation
        rides20Reactivation: 1500,
        hours130Reactivation: 900
    });

    // Milestone thresholds (loaded from API, with defaults)
    const [milestoneThresholds, setMilestoneThresholds] = useState({
        milestoneThreshold1: 5,
        milestoneThreshold2: 15,
        milestoneThreshold3: 35,
        milestoneThreshold4: 55
    });

    const token = localStorage.getItem('affiliateToken');
    const user = JSON.parse(localStorage.getItem('affiliateUser') || '{}');

    const fetchDashboard = useCallback(async () => {
        if (!token) {
            navigate('/affilies/connexion');
            return;
        }

        setLoading(true);
        try {
            const response = await affiliateService.getDashboard(token);
            if (response.success) {
                if (response.requireRelogin) {
                    message.success('Votre compte a été approuvé ! Veuillez vous reconnecter.');
                    localStorage.removeItem('affiliateToken');
                    localStorage.removeItem('affiliateUser');
                    navigate('/affilies/connexion');
                    return;
                }
                setDashboardData(response.data);
                if (response.data?.milestoneThresholds) {
                    setMilestoneThresholds(prev => ({ ...prev, ...response.data.milestoneThresholds }));
                }
            } else {
                message.error('Erreur lors du chargement');
            }
        } catch (error) {
            console.error('Erreur dashboard:', error);
            if (error.message?.includes('Token') || error.status === 401) {
                localStorage.removeItem('affiliateToken');
                localStorage.removeItem('affiliateUser');
                navigate('/affilies/connexion');
            }
        } finally {
            setLoading(false);
        }
    }, [token, navigate]);

    useEffect(() => {
        fetchDashboard();
        loadCities();
    }, [fetchDashboard]);

    const loadCities = async () => {
        try {
            const response = await cityService.getActiveCities();
            if (response.success) {
                setCities(response.data || []);
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    };

    // Charger les chauffeurs avec progression au démarrage (pour le top 3)
    useEffect(() => {
        if (token) {
            loadDriversWithProgress();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleLogout = () => {
        localStorage.removeItem('affiliateToken');
        localStorage.removeItem('affiliateUser');
        message.success('Déconnexion réussie');
        navigate('/affilies/connexion');
    };

    const copyCode = () => {
        const code = dashboardData?.affiliate?.referralCode;
        if (code) {
            navigator.clipboard.writeText(code);
            message.success('Code copié !');
        }
    };

    const formatPhone = (phone) => {
        if (!phone) return '-';
        const cleanPhone = phone.replace(/\s/g, '');
        if (cleanPhone.startsWith('+225')) {
            const number = cleanPhone.substring(4);
            return `+225 ${number.substring(0, 2)} ${number.substring(2, 4)} ${number.substring(4, 6)} ${number.substring(6)}`;
        }
        if (cleanPhone.startsWith('225')) {
            const number = cleanPhone.substring(3);
            return `+225 ${number.substring(0, 2)} ${number.substring(2, 4)} ${number.substring(4, 6)} ${number.substring(6)}`;
        }
        if (cleanPhone.startsWith('0')) {
            return `+225 ${cleanPhone.substring(0, 2)} ${cleanPhone.substring(2, 4)} ${cleanPhone.substring(4, 6)} ${cleanPhone.substring(6)}`;
        }
        return `+225 ${cleanPhone.substring(0, 2)} ${cleanPhone.substring(2, 4)} ${cleanPhone.substring(4, 6)} ${cleanPhone.substring(6)}`;
    };

    const handleAddDriver = async (values) => {
        setAddingDriver(true);
        try {
            const phone = values.countryCode + values.phone.replace(/\s/g, '');
            const response = await affiliateService.registerDriver(token, {
                firstName: values.firstName.toUpperCase(),
                lastName: values.lastName.toUpperCase(),
                phone,
                city: values.city
            });

            if (response.success) {
                message.success(response.message || 'Chauffeur enregistré avec succès !');
                setDriverModalVisible(false);
                driverForm.resetFields();
                fetchDashboard();
            } else {
                message.error(response.message || 'Erreur lors de l\'enregistrement');
            }
        } catch (error) {
            message.error(error.message || 'Erreur lors de l\'enregistrement');
        } finally {
            setAddingDriver(false);
        }
    };

    const handleUpdatePassword = async (values) => {
        setUpdating(true);
        try {
            const response = await affiliateService.updatePassword(token, values.currentPassword, values.newPassword);
            if (response.success) {
                message.success('Mot de passe mis à jour');
                setPasswordModalVisible(false);
                passwordForm.resetFields();
            } else {
                message.error(response.message || 'Erreur');
            }
        } catch (error) {
            message.error(error.message || 'Erreur');
        } finally {
            setUpdating(false);
        }
    };

    // Revenue functions
    const loadCommissionsDashboard = async () => {
        try {
            const response = await affiliateService.getCommissionsDashboard(token);
            if (response.success) {
                setCommissionsData(response.data);
                // Mettre à jour les taux de commission depuis l'API (fusionner avec les valeurs par défaut)
                if (response.data?.rates) {
                    setCommissionRates(prev => ({ ...prev, ...response.data.rates }));
                }
                if (response.data?.milestoneThresholds) {
                    setMilestoneThresholds(prev => ({ ...prev, ...response.data.milestoneThresholds }));
                }
            }
        } catch (error) {
            console.error('Erreur chargement commissions dashboard:', error);
        }
    };

    const loadDriversWithProgress = async () => {
        setLoadingRevenues(true);
        try {
            const response = await affiliateService.getDriversWithProgress(token, { limit: 200 });
            if (response.success) {
                setDriversProgress(response.data?.drivers || []);
                if (response.data?.stats) {
                    setCommissionsData(prev => ({ ...prev, ...response.data.stats }));
                }
                if (response.data?.milestoneThresholds) {
                    setMilestoneThresholds(prev => ({ ...prev, ...response.data.milestoneThresholds }));
                }
            }
        } catch (error) {
            message.error('Erreur lors du chargement des progressions');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const loadCommissionsHistory = async () => {
        setLoadingRevenues(true);
        try {
            const response = await affiliateService.getCommissionsHistory(token);
            if (response.success) {
                setCommissionsHistory(response.data?.commissions || []);
            }
        } catch (error) {
            message.error('Erreur lors du chargement de l\'historique');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const loadDriverProgress = async (driverId) => {
        setLoadingRevenues(true);
        try {
            const response = await affiliateService.getDriverProgress(token, driverId);
            if (response.success) {
                setSelectedDriverProgress(response.data);
                // Mettre à jour les taux de commission depuis l'API (fusionner avec les valeurs par défaut)
                if (response.data?.rates) {
                    setCommissionRates(prev => ({ ...prev, ...response.data.rates }));
                }
                if (response.data?.milestoneThresholds) {
                    setMilestoneThresholds(prev => ({ ...prev, ...response.data.milestoneThresholds }));
                }
            }
        } catch (error) {
            message.error('Erreur lors du chargement des détails');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const loadRevenues130h = async () => {
        setLoadingRevenues(true);
        try {
            const response = await affiliateService.getRevenues130h(token);
            if (response.success) {
                setRevenues130h(response.data?.records || []);
                setRevenues130hPeriodStats(response.data?.periodStats || []);
                if (response.data?.hoursThreshold) {
                    setHoursThreshold(response.data.hoursThreshold);
                }
                if (response.data?.commissionAmount) {
                    setCommissionRates(prev => ({ ...prev, hours130: response.data.commissionAmount }));
                }
            }
        } catch (error) {
            console.error('Erreur chargement revenus 130h:', error);
            message.error('Erreur lors du chargement des revenus 130h');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const loadCollectedRevenues = async () => {
        setLoadingRevenues(true);
        try {
            const response = await affiliateService.getCollectedRevenues(token);
            if (response.success) {
                setCollectedRevenues(response.data?.commissions || []);
            }
        } catch (error) {
            console.error('Erreur chargement revenus encaissés:', error);
            message.error('Erreur lors du chargement des revenus encaissés');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const loadBonusRecharges = async () => {
        setLoadingBonusRecharge(true);
        try {
            const response = await affiliateService.getSameDayBonuses(token);
            if (response.success) {
                setBonusRecharges(response.data || []);
            }
        } catch (error) {
            console.error('Erreur chargement bonus recharge:', error);
            message.error('Erreur lors du chargement des bonus recharge');
        } finally {
            setLoadingBonusRecharge(false);
        }
    };

    const loadMonthlyRetentions = async () => {
        setLoadingMonthlyRetentions(true);
        try {
            const response = await affiliateService.getMonthlyRetentions(token);
            if (response.success) {
                setMonthlyRetentions(response.data?.retentions || []);
                setMonthlyRetentionsPeriodStats(response.data?.periodStats || []);
            }
        } catch (error) {
            console.error('Erreur chargement rétentions mensuelles:', error);
            message.error('Erreur lors du chargement des rétentions mensuelles');
        } finally {
            setLoadingMonthlyRetentions(false);
        }
    };

    const handleMenuClick = (e) => {
        setSelectedMenu(e.key);
        if (e.key === 'revenues-pending') loadDriversWithProgress();
        if (e.key === 'revenues-130h') loadRevenues130h();
        if (e.key === 'revenues-collected') loadCollectedRevenues();
        if (e.key === 'revenues-bonus-recharge') loadBonusRecharges();
        if (e.key === 'revenues-monthly-retentions') loadMonthlyRetentions();
    };

    // Columns - Mobile optimized
    const referralColumns = [
        {
            title: 'Chauffeur',
            key: 'driver',
            render: (_, record) => (
                <div className="mobile-cell-main">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                        <div>
                            <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
                            <div className="mobile-cell-sub">{formatPhone(record.phone)}</div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: 'Téléphone',
            key: 'phone',
            responsive: ['md'],
            render: (_, record) => formatPhone(record.phone),
        },
        {
            title: 'Date',
            key: 'date',
            render: (_, record) => (
                <span className="mobile-date">
                    {dayjs(record.referredBy?.referredAt || record.createdAt).format('DD/MM/YY')}
                </span>
            )
        },
        {
            title: 'Statut',
            key: 'status',
            render: (_, record) => (
                <Tag color={record.isActive !== false ? 'green' : 'default'}>
                    {record.isActive !== false ? 'Actif' : 'Inactif'}
                </Tag>
            )
        }
    ];

    const commissionColumns = [
        {
            title: 'Commission',
            key: 'commission',
            render: (_, record) => {
                const types = {
                    referral_bonus: 'Parrainage',
                    activity_bonus: 'Activité',
                    monthly_bonus: 'Mensuel',
                    special_bonus: 'Spécial'
                };
                return (
                    <div className="mobile-cell-main">
                        <div style={{ fontWeight: 500 }}>{types[record.type] || record.type}</div>
                        <div className="mobile-cell-sub">{dayjs(record.createdAt).format('DD/MM/YY')}</div>
                    </div>
                );
            }
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'date',
            responsive: ['md'],
            render: (date) => dayjs(date).format('DD/MM/YYYY')
        },
        {
            title: 'Montant',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => <Text strong style={{ color: '#22c55e' }}>{amount?.toLocaleString()} F</Text>
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const config = {
                    pending: { color: 'warning', text: 'Attente' },
                    approved: { color: 'processing', text: 'Approuvé' },
                    paid: { color: 'success', text: 'Payé' },
                    cancelled: { color: 'error', text: 'Annulé' }
                };
                const c = config[status] || config.pending;
                return <Tag color={c.color}>{c.text}</Tag>;
            }
        }
    ];

    if (loading) {
        return (
            <div className="affiliate-dashboard-loading">
                <Spin size="large" />
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="affiliate-dashboard-loading">
                <Text>Impossible de charger les données</Text>
            </div>
        );
    }

    const { affiliate, stats, recentReferrals, recentCommissions, settings } = dashboardData;

    // Status screens
    if (affiliate?.status === 'pending') {
        return (
            <div className="affiliate-status-page">
                <Card className="status-card pending">
                    <div className="status-icon"><HourglassOutlined /></div>
                    <Title level={2}>Inscription en cours de traitement</Title>
                    <Paragraph>
                        Bonjour <strong>{affiliate?.firstName}</strong>, votre demande d'affiliation a bien été reçue
                        et est actuellement en cours d'examen par notre équipe.
                    </Paragraph>
                    <div className="status-timeline">
                        <div className="timeline-step completed">
                            <div className="step-dot"><CheckCircleOutlined /></div>
                            <span>Inscription reçue</span>
                        </div>
                        <div className="timeline-step active">
                            <div className="step-dot"><ClockCircleOutlined /></div>
                            <span>Examen en cours</span>
                        </div>
                        <div className="timeline-step">
                            <div className="step-dot">3</div>
                            <span>Validation</span>
                        </div>
                    </div>
                    <Button icon={<LogoutOutlined />} onClick={handleLogout} style={{ marginTop: 24 }}>
                        Se déconnecter
                    </Button>
                </Card>
            </div>
        );
    }

    if (affiliate?.status === 'suspended' || affiliate?.status === 'rejected') {
        return (
            <div className="affiliate-status-page">
                <Card className="status-card error">
                    <div className="status-icon error"><ExclamationCircleOutlined /></div>
                    <Title level={2}>{affiliate?.status === 'suspended' ? 'Compte suspendu' : 'Candidature refusée'}</Title>
                    <Paragraph>
                        {affiliate?.status === 'suspended'
                            ? 'Votre compte affilié a été suspendu. Contactez le support.'
                            : affiliate?.rejectionReason || 'Votre candidature n\'a pas été retenue.'}
                    </Paragraph>
                    <Button icon={<LogoutOutlined />} onClick={handleLogout} style={{ marginTop: 24 }}>
                        Se déconnecter
                    </Button>
                </Card>
            </div>
        );
    }

    // Page titles
    const getPageTitle = () => {
        switch (selectedMenu) {
            case 'referrals':
                return { title: 'Mes Chauffeurs', subtitle: 'Gérez les chauffeurs que vous avez recrutés' };
            case 'commissions':
                return { title: 'Mes Commissions', subtitle: 'Historique de vos revenus' };
            case 'revenues-pending':
                return { title: 'Revenus en Cours', subtitle: 'Suivi des commissions en attente' };
            case 'revenues-collected':
                return { title: 'Revenus Encaissés', subtitle: 'Historique des commissions payées' };
            case 'revenues-130h':
                return { title: `Revenus ${hoursThreshold}h`, subtitle: `Commissions mensuelles des ${hoursThreshold} heures` };
            case 'revenues-bonus-recharge':
                return { title: 'Bonus Recharge Jour', subtitle: 'Bonus de 2 000 F quand 2+ chauffeurs recrutés le même jour rechargent 2 000 F' };
            case 'revenues-monthly-retentions':
                return { title: 'Rétentions Mensuelles', subtitle: 'Commission de 1% sur les recharges mensuelles de vos chauffeurs' };
            case 'driver-progress':
                const driverName = selectedDriverProgress?.driver
                    ? `${selectedDriverProgress.driver.firstName} ${selectedDriverProgress.driver.lastName}`
                    : selectedDriverProgress?.driverId
                        ? `${selectedDriverProgress.driverId.firstName} ${selectedDriverProgress.driverId.lastName}`
                        : '';
                return { title: 'Détails Chauffeur', subtitle: driverName };
            case 'profile':
                return { title: 'Mon Profil', subtitle: 'Gérez vos informations personnelles' };
            default:
                return { title: 'Tableau de Bord', subtitle: 'Vue d\'ensemble de vos activités' };
        }
    };

    const pageInfo = getPageTitle();

    // Render functions
    const renderDashboardContent = () => {
        const totalPotential = (stats?.totalReferred || 0) * 6300; // Potentiel total par chauffeur
        const completionRate = totalPotential > 0 ? Math.round(((stats?.totalCommissions || 0) + (stats?.pendingCommissions || 0)) / totalPotential * 100) : 0;

        return (
            <div className="dashboard-content-wrapper">
                {/* Stats Cards */}
                <Row gutter={[16, 16]} className="stats-cards-row" style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={12} md={6}>
                        <Card
                            style={{
                                borderRadius: 12,
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                height: '100%'
                            }}
                            bodyStyle={{ padding: '20px 16px' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: 'linear-gradient(135deg, #1890ff15 0%, #1890ff25 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <TeamOutlined style={{ fontSize: 22, color: '#1890ff' }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Chauffeurs</div>
                                    <div style={{ fontSize: 26, fontWeight: 700, color: '#1890ff', lineHeight: 1.2 }}>
                                        {stats?.totalReferred || 0}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={12} sm={12} md={6}>
                        <Card
                            style={{
                                borderRadius: 12,
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                height: '100%'
                            }}
                            bodyStyle={{ padding: '20px 16px' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: 'linear-gradient(135deg, #52c41a15 0%, #52c41a25 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <CalendarOutlined style={{ fontSize: 22, color: '#52c41a' }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Ce mois</div>
                                    <div style={{ fontSize: 26, fontWeight: 700, color: '#52c41a', lineHeight: 1.2 }}>
                                        {stats?.thisMonthReferrals || 0}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={12} sm={12} md={6}>
                        <Card
                            style={{
                                borderRadius: 12,
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                cursor: 'pointer',
                                height: '100%',
                                transition: 'transform 0.2s'
                            }}
                            bodyStyle={{ padding: '20px 16px' }}
                            onClick={() => { setSelectedMenu('revenues-collected'); loadCollectedRevenues(); }}
                            hoverable
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: 'linear-gradient(135deg, #D4A85315 0%, #D4A85325 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <CheckCircleOutlined style={{ fontSize: 22, color: '#D4A853' }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Revenus encaissés</div>
                                    <div style={{ fontSize: 22, fontWeight: 700, color: '#D4A853', lineHeight: 1.2 }}>
                                        {(stats?.totalCommissions || 0).toLocaleString()} <span style={{ fontSize: 14, fontWeight: 500 }}>F</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={12} sm={12} md={6}>
                        <Card
                            style={{
                                borderRadius: 12,
                                border: '2px solid #D4A853',
                                background: 'linear-gradient(135deg, #D4A853 0%, #b8954a 100%)',
                                cursor: 'pointer',
                                height: '100%',
                                transition: 'transform 0.2s'
                            }}
                            bodyStyle={{ padding: '20px 16px' }}
                            onClick={() => setDriverModalVisible(true)}
                            hoverable
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: 'rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <UserAddOutlined style={{ fontSize: 22, color: 'white' }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: 'white' }}>Inscrire</div>
                                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                                        un chauffeur
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Top Drivers Table - Full Width */}
                <Card
                    className="top-drivers-card"
                    style={{
                        borderRadius: 16,
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        marginBottom: 24
                    }}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #52c41a15 0%, #52c41a25 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <TrophyOutlined style={{ fontSize: 18, color: '#52c41a' }} />
                            </div>
                            <span style={{ fontWeight: 600 }}>Top chauffeurs à encourager</span>
                        </div>
                    }
                    extra={
                        <Button
                            type="link"
                            onClick={() => { setSelectedMenu('revenues-pending'); loadDriversWithProgress(); }}
                            style={{ color: '#52c41a', fontWeight: 500 }}
                        >
                            Voir tout →
                        </Button>
                    }
                    bodyStyle={{ padding: 0 }}
                >
                    <Table
                        dataSource={driversProgress
                            .filter(d => d.status !== 'expired' && (d.currentStats?.totalRides || 0) >= 0 && (d.currentStats?.totalRides || 0) < 55)
                            .sort((a, b) => (b.currentStats?.totalRides || 0) - (a.currentStats?.totalRides || 0))
                            .slice(0, 5)
                        }
                        rowKey={(record) => record._id || record.driverId?._id}
                        pagination={false}
                        locale={{ emptyText: 'Aucun chauffeur en progression' }}
                        columns={[
                            {
                                title: 'Chauffeur',
                                key: 'driver',
                                render: (_, record) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Avatar
                                            size={40}
                                            style={{
                                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                                fontWeight: 600
                                            }}
                                        >
                                            {(record.driverId?.firstName || record.firstName)?.charAt(0)}{(record.driverId?.lastName || record.lastName)?.charAt(0)}
                                        </Avatar>
                                        <div>
                                            <div style={{ fontWeight: 600, color: '#262626' }}>
                                                {record.driverId?.firstName || record.firstName} {record.driverId?.lastName || record.lastName}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {record.driverId?.phone || record.phone}
                                            </div>
                                        </div>
                                    </div>
                                )
                            },
                            {
                                title: 'Courses',
                                key: 'rides',
                                width: 80,
                                align: 'center',
                                render: (_, record) => (
                                    <span style={{ fontWeight: 600, fontSize: 16, color: '#262626' }}>
                                        {record.currentStats?.totalRides || 0}
                                    </span>
                                )
                            },
                            {
                                title: 'Progression',
                                key: 'progression',
                                render: (_, record) => {
                                    const milestones = record.milestones || {};
                                    const rides = record.currentStats?.totalRides || 0;
                                    const isReactivation = record.driverType === 'reactivation';

                                    // Utiliser les seuils de la ville du chauffeur si disponibles
                                    const driverThresholds = record.milestoneThresholds || milestoneThresholds;
                                    const t1 = driverThresholds.milestoneThreshold1 || 5;
                                    const t2 = driverThresholds.milestoneThreshold2 || 15;
                                    const t3 = driverThresholds.milestoneThreshold3 || 35;
                                    const t4 = driverThresholds.milestoneThreshold4 || 55;
                                    const getTagColorNew = (achieved, threshold) => {
                                        if (achieved) return 'green';
                                        if (threshold === t1 && rides < t1) return 'orange';
                                        if (threshold === t2 && rides >= t1 && rides < t2) return 'orange';
                                        if (threshold === t3 && rides >= t2 && rides < t3) return 'orange';
                                        if (threshold === t4 && rides >= t3 && rides < t4) return 'orange';
                                        return 'default';
                                    };

                                    // Réactivation
                                    const getTagColorReactivation = (achieved, threshold) => {
                                        if (achieved) return 'green';
                                        if (threshold === 20 && rides < 20) return 'orange';
                                        return 'default';
                                    };

                                    if (isReactivation) {
                                        return (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                                <Tag color={milestones.accountCreation?.achieved ? 'green' : 'default'}>Création</Tag>
                                                <Tag color={getTagColorReactivation(milestones.rides20Reactivation?.achieved, 20)}>20 courses</Tag>
                                                <Tag color={milestones.hours130Reactivation?.achieved ? 'green' : 'default'}>{hoursThreshold}h</Tag>
                                                <Tag color="orange" style={{ fontSize: 10 }}>Réactiv.</Tag>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            <Tag color={milestones.accountCreation?.achieved ? 'green' : 'default'}>Création</Tag>
                                            <Tag color={getTagColorNew(milestones.rides5?.achieved, t1)}>{t1} courses</Tag>
                                            <Tag color={getTagColorNew(milestones.rides15?.achieved, t2)}>{t2} courses</Tag>
                                            <Tag color={getTagColorNew(milestones.rides35?.achieved, t3)}>{t3} courses</Tag>
                                            <Tag color={getTagColorNew(milestones.rides55?.achieved, t4)}>{t4} courses</Tag>
                                        </div>
                                    );
                                }
                            },
                            {
                                title: 'Période',
                                key: 'period',
                                width: 200,
                                render: (_, record) => {
                                    const startDate = record.trackingStartDate ? dayjs(record.trackingStartDate) : null;
                                    const endDate = record.activationWindowEnd ? dayjs(record.activationWindowEnd) : null;
                                    return (
                                        <div style={{ fontSize: 12, color: '#595959' }}>
                                            <div>{startDate ? startDate.format('DD/MM/YYYY HH:mm') : '-'}</div>
                                            <div style={{ color: '#8c8c8c' }}>→ {endDate ? endDate.format('DD/MM/YYYY HH:mm') : '-'}</div>
                                        </div>
                                    );
                                }
                            },
                            {
                                title: 'Restant',
                                key: 'remaining',
                                width: 100,
                                align: 'center',
                                render: (_, record) => {
                                    const endDate = record.activationWindowEnd ? dayjs(record.activationWindowEnd) : null;
                                    if (!endDate) return <Tag>-</Tag>;
                                    const now = dayjs();
                                    const totalHours = endDate.diff(now, 'hour');
                                    const daysRemaining = Math.floor(totalHours / 24);
                                    const hoursRemaining = totalHours % 24;
                                    if (totalHours < 0) {
                                        return <Tag color="red">Expiré</Tag>;
                                    } else if (totalHours < 24) {
                                        return <Tag color="orange">{hoursRemaining}h</Tag>;
                                    } else if (daysRemaining <= 2) {
                                        return <Tag color="orange">{daysRemaining}j {hoursRemaining}h</Tag>;
                                    } else {
                                        return <Tag color="blue">{daysRemaining}j {hoursRemaining}h</Tag>;
                                    }
                                }
                            },
                            {
                                title: 'Gains',
                                key: 'gains',
                                width: 100,
                                align: 'right',
                                render: (_, record) => {
                                    const total = calculateDriverRevenue(record.milestones, record.driverType);
                                    return (
                                        <span style={{ fontWeight: 600, color: '#52c41a' }}>
                                            {total.toLocaleString()} F
                                        </span>
                                    );
                                }
                            }
                        ]}
                    />
                </Card>

                {/* Recent Referrals */}
                <Card
                    className="recent-referrals-card"
                    style={{
                        borderRadius: 16,
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #D4A85315 0%, #D4A85325 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <TeamOutlined style={{ fontSize: 18, color: '#D4A853' }} />
                            </div>
                            <span style={{ fontWeight: 600 }}>Derniers parrainages</span>
                        </div>
                    }
                    extra={
                        <Button
                            type="link"
                            onClick={() => setSelectedMenu('referrals')}
                            style={{ color: '#D4A853', fontWeight: 500 }}
                        >
                            Voir tout →
                        </Button>
                    }
                    bodyStyle={{ padding: 0 }}
                >
                    {recentReferrals?.length > 0 ? (
                        <Table
                            columns={[
                                {
                                    title: 'Chauffeur',
                                    key: 'driver',
                                    render: (_, record) => (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <Avatar
                                                size={40}
                                                style={{
                                                    background: 'linear-gradient(135deg, #D4A853 0%, #b8954a 100%)',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {record.firstName?.charAt(0)}{record.lastName?.charAt(0)}
                                            </Avatar>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#262626' }}>
                                                    {record.firstName} {record.lastName}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                    {formatPhone(record.phone)}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    title: 'Date',
                                    key: 'date',
                                    responsive: ['md'],
                                    render: (_, record) => (
                                        <span style={{ color: '#595959' }}>
                                            {dayjs(record.referredBy?.referredAt || record.createdAt).format('DD/MM/YYYY')}
                                        </span>
                                    )
                                },
                                {
                                    title: 'Statut',
                                    key: 'status',
                                    render: (_, record) => (
                                        <Tag
                                            color={record.isActive !== false ? 'success' : 'default'}
                                            style={{ borderRadius: 6 }}
                                        >
                                            {record.isActive !== false ? 'Actif' : 'Inactif'}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Action',
                                    key: 'action',
                                    width: 120,
                                    render: (_, record) => (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => {
                                                // Trouver le driver progress correspondant ou charger
                                                const driverProgress = driversProgress.find(
                                                    dp => dp.driverId?._id === record._id
                                                );
                                                if (driverProgress) {
                                                    setSelectedDriverProgress(driverProgress);
                                                    setSelectedMenu('driver-progress');
                                                    loadDriverProgress(record._id);
                                                } else {
                                                    // Charger directement
                                                    setSelectedMenu('revenues-pending');
                                                    loadDriversWithProgress();
                                                }
                                            }}
                                            style={{ color: '#1890ff', padding: 0 }}
                                        >
                                            Voir détail
                                        </Button>
                                    )
                                }
                            ]}
                            dataSource={recentReferrals?.slice(0, 5)}
                            rowKey="_id"
                            pagination={false}
                            scroll={{ x: 500 }}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                                        <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                                        <div style={{ color: '#8c8c8c' }}>Aucun parrainage pour le moment</div>
                                        <Button
                                            type="primary"
                                            icon={<UserAddOutlined />}
                                            style={{ marginTop: 16, background: '#D4A853', borderColor: '#D4A853' }}
                                            onClick={() => setDriverModalVisible(true)}
                                        >
                                            Inscrire mon premier chauffeur
                                        </Button>
                                    </div>
                                )
                            }}
                            size="middle"
                        />
                    ) : (
                        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                            <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <div style={{ color: '#8c8c8c', marginBottom: 16 }}>Aucun parrainage pour le moment</div>
                            <Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                style={{ background: '#D4A853', borderColor: '#D4A853' }}
                                onClick={() => setDriverModalVisible(true)}
                            >
                                Inscrire mon premier chauffeur
                            </Button>
                        </div>
                    )}
                </Card>
            </div>
        );
    };

    const renderReferralsContent = () => {
        const activeDrivers = recentReferrals?.filter(d => d.isActive !== false).length || 0;
        const inactiveDrivers = (recentReferrals?.length || 0) - activeDrivers;

        // Calculate stats for this week
        const startOfWeek = dayjs().startOf('week');
        const endOfWeek = dayjs().endOf('week');
        const weekPeriod = `${startOfWeek.format('DD/MM')} - ${endOfWeek.format('DD/MM')}`;

        // Drivers linked to a vehicle (have driverProfilId)
        const driversWithVehicle = (driversProgress || []).filter(d => {
            return d.driverProfilId || d.driverId?.driverProfilId;
        }).length;

        // Drivers who started rides this week (have at least 1 ride and tracking started this week)
        const driversActiveThisWeek = (driversProgress || []).filter(d => {
            const rides = d.currentStats?.totalRides || 0;
            const trackingStart = d.trackingStartDate ? dayjs(d.trackingStartDate) : null;
            return rides > 0 && trackingStart && trackingStart.isAfter(startOfWeek) && trackingStart.isBefore(endOfWeek);
        }).length;

        return (
            <div className="drivers-page-content">
                {/* Stats Row */}
                <Row gutter={[16, 16]} className="referrals-stats-row" style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap' }}>
                    <Col xs={12} md={6} style={{ display: 'flex' }}>
                        <Card
                            style={{
                                borderRadius: 12,
                                background: 'linear-gradient(135deg, #D4A853 0%, #b8954a 100%)',
                                border: 'none',
                                cursor: 'pointer',
                                textAlign: 'center',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            bodyStyle={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}
                            onClick={() => setDriverModalVisible(true)}
                            hoverable
                        >
                            <UserAddOutlined style={{ fontSize: 28, color: 'white' }} />
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'white', marginTop: 8 }}>Inscrire un chauffeur</div>
                        </Card>
                    </Col>
                    <Col xs={12} md={6} style={{ display: 'flex' }}>
                        <Card
                            style={{
                                borderRadius: 12,
                                border: '1px solid #f0f0f0',
                                boxShadow: 'none',
                                textAlign: 'center',
                                width: '100%'
                            }}
                            bodyStyle={{ padding: '16px 12px' }}
                        >
                            <div style={{ fontSize: 26, fontWeight: 700, color: '#262626' }}>
                                {driversWithVehicle}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Liés à un véhicule</div>
                            <div style={{ fontSize: 10, color: '#bfbfbf', marginTop: 2 }}>Prêts à conduire</div>
                        </Card>
                    </Col>
                    <Col xs={12} md={6} style={{ display: 'flex' }}>
                        <Card
                            style={{
                                borderRadius: 12,
                                border: '1px solid #f0f0f0',
                                boxShadow: 'none',
                                textAlign: 'center',
                                width: '100%'
                            }}
                            bodyStyle={{ padding: '16px 12px' }}
                        >
                            <div style={{ fontSize: 26, fontWeight: 700, color: '#262626' }}>
                                {commissionsData?.totalCount || driversProgress?.length || recentReferrals?.length || 0}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total chauffeurs</div>
                            <div style={{ fontSize: 10, color: '#bfbfbf', marginTop: 2 }}>Depuis le début</div>
                        </Card>
                    </Col>
                    <Col xs={12} md={6} style={{ display: 'flex' }}>
                        <Card
                            style={{
                                borderRadius: 12,
                                border: '1px solid #f0f0f0',
                                boxShadow: 'none',
                                textAlign: 'center',
                                width: '100%'
                            }}
                            bodyStyle={{ padding: '16px 12px' }}
                        >
                            <div style={{ fontSize: 26, fontWeight: 700, color: '#262626' }}>
                                {driversActiveThisWeek}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Actifs cette semaine</div>
                            <div style={{ fontSize: 10, color: '#bfbfbf', marginTop: 2 }}>{weekPeriod}</div>
                        </Card>
                    </Col>
                </Row>

                {/* Drivers Table */}
                <Card
                    className="drivers-list-card"
                    style={{
                        borderRadius: 16,
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #D4A85315 0%, #D4A85325 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CarOutlined style={{ fontSize: 18, color: '#D4A853' }} />
                            </div>
                            <span style={{ fontWeight: 600 }}>Liste de mes chauffeurs</span>
                        </div>
                    }
                    extra={
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Input
                                placeholder="Rechercher..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={referralsSearchTerm}
                                onChange={(e) => setReferralsSearchTerm(e.target.value)}
                                style={{ width: 200 }}
                                allowClear
                            />
                            <Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                onClick={() => setDriverModalVisible(true)}
                                style={{
                                    background: '#D4A853',
                                    borderColor: '#D4A853',
                                    borderRadius: 8
                                }}
                            >
                                <span className="btn-text-desktop">Inscrire un chauffeur</span>
                                <span className="btn-text-mobile" style={{ display: 'none' }}>Inscrire</span>
                            </Button>
                        </div>
                    }
                    bodyStyle={{ padding: 0 }}
                >
                    {(driversProgress?.length > 0 || recentReferrals?.length > 0) ? (
                        <Table
                            columns={[
                                {
                                    title: 'Chauffeur',
                                    key: 'driver',
                                    render: (_, record) => {
                                        const driver = record.driverId || record;
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <Avatar
                                                    size={44}
                                                    style={{
                                                        background: driver.isActive !== false
                                                            ? 'linear-gradient(135deg, #D4A853 0%, #b8954a 100%)'
                                                            : '#d9d9d9',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {driver.firstName?.charAt(0)}{driver.lastName?.charAt(0)}
                                                </Avatar>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: '#262626', fontSize: 14 }}>
                                                        {driver.firstName} {driver.lastName}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                        {formatPhone(driver.phone)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                },
                                {
                                    title: 'Téléphone',
                                    key: 'phone',
                                    responsive: ['md'],
                                    render: (_, record) => {
                                        const driver = record.driverId || record;
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <PhoneOutlined style={{ color: '#8c8c8c' }} />
                                                <span>{formatPhone(driver.phone)}</span>
                                            </div>
                                        );
                                    }
                                },
                                {
                                    title: 'Date d\'inscription',
                                    key: 'date',
                                    responsive: ['lg'],
                                    render: (_, record) => {
                                        const driver = record.driverId || record;
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <CalendarOutlined style={{ color: '#8c8c8c' }} />
                                                <span style={{ color: '#595959' }}>
                                                    {dayjs(record.trackingStartDate || driver.referredBy?.referredAt || driver.createdAt).format('DD/MM/YYYY')}
                                                </span>
                                            </div>
                                        );
                                    }
                                },
                                {
                                    title: 'Statut',
                                    key: 'status',
                                    render: (_, record) => {
                                        const hasVehicle = record.driverProfilId || record.driverId?.driverProfilId;
                                        return (
                                            <Tag
                                                color={hasVehicle ? 'success' : 'warning'}
                                                style={{
                                                    borderRadius: 6,
                                                    padding: '4px 12px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {hasVehicle ? '● Véhicule lié' : '○ Non lié'}
                                            </Tag>
                                        );
                                    }
                                },
                                {
                                    title: 'Actions',
                                    key: 'actions',
                                    width: 100,
                                    render: (_, record) => {
                                        const driver = record.driverId || record;
                                        return (
                                            <Button
                                                type="text"
                                                icon={<EyeOutlined style={{ color: '#D4A853' }} />}
                                                onClick={() => {
                                                    setSelectedDriverProgress(record);
                                                    setSelectedMenu('driver-progress');
                                                    loadDriverProgress(driver._id);
                                                }}
                                                style={{ borderRadius: 8, color: '#D4A853' }}
                                            >
                                                Détails
                                            </Button>
                                        );
                                    }
                                }
                            ]}
                            dataSource={(driversProgress?.length > 0 ? driversProgress : recentReferrals || []).filter(d => {
                                if (!referralsSearchTerm) return true;
                                const search = referralsSearchTerm.toLowerCase();
                                const driver = d.driverId || d;
                                const firstName = (driver.firstName || '').toLowerCase();
                                const lastName = (driver.lastName || '').toLowerCase();
                                const phone = (driver.phone || '').toLowerCase();
                                return firstName.includes(search) || lastName.includes(search) || phone.includes(search);
                            })}
                            rowKey="_id"
                            pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '30', '50', '100'],
                                showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} chauffeur${total > 1 ? 's' : ''}`
                            }}
                            locale={{ emptyText: referralsSearchTerm ? 'Aucun résultat trouvé' : 'Aucun chauffeur' }}
                            scroll={{ x: 600 }}
                            size="middle"
                        />
                    ) : (
                        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                            <div style={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #D4A85315 0%, #D4A85325 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px'
                            }}>
                                <CarOutlined style={{ fontSize: 36, color: '#D4A853' }} />
                            </div>
                            <Title level={4} style={{ color: '#262626', marginBottom: 8 }}>
                                Aucun chauffeur inscrit
                            </Title>
                            <Text style={{ color: '#8c8c8c', display: 'block', marginBottom: 24 }}>
                                Commencez à recruter des chauffeurs et gagnez des commissions
                            </Text>
                            <Button
                                type="primary"
                                size="large"
                                icon={<UserAddOutlined />}
                                style={{
                                    background: '#D4A853',
                                    borderColor: '#D4A853',
                                    borderRadius: 8,
                                    height: 48,
                                    paddingLeft: 24,
                                    paddingRight: 24
                                }}
                                onClick={() => setDriverModalVisible(true)}
                            >
                                Inscrire mon premier chauffeur
                            </Button>
                        </div>
                    )}
                </Card>

                {/* Commission Info Card */}
                <Card
                    style={{
                        borderRadius: 16,
                        marginTop: 24,
                        border: '2px solid #D4A85330',
                        background: 'linear-gradient(135deg, #fffbf0 0%, #fff9e6 100%)'
                    }}
                    bodyStyle={{ padding: 20 }}
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: '#D4A853',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <DollarOutlined style={{ fontSize: 24, color: 'white' }} />
                                </div>
                                <div>
                                    <Text strong style={{ fontSize: 16, color: '#262626' }}>
                                        Gagnez jusqu'à 6 300 F par chauffeur
                                    </Text>
                                    <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 4 }}>
                                        Chaque chauffeur que vous inscrivez vous rapporte des commissions à chaque palier atteint :
                                        création de compte, {milestoneThresholds.milestoneThreshold1}, {milestoneThresholds.milestoneThreshold2}, {milestoneThresholds.milestoneThreshold3} et {milestoneThresholds.milestoneThreshold4} courses complétées.
                                    </div>
                                </div>
                            </div>
                        </Col>
                        {/* Commenté pour le moment
                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                            <Button
                                type="link"
                                style={{ color: '#D4A853', fontWeight: 600 }}
                                onClick={() => setSelectedMenu('commissions')}
                            >
                                Voir le détail des commissions →
                            </Button>
                        </Col>
                        */}
                    </Row>
                </Card>
            </div>
        );
    };

    const renderCommissionsContent = () => (
        <>
            {/* Commission Info */}
            <Card className="commission-info-card" style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                    <Col xs={24} md={12}>
                        <div className="commission-rate">
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                            <div>
                                <Text type="secondary">Commission par chauffeur</Text>
                                <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                                    {(settings?.commissionAmount || 500)?.toLocaleString()} FCFA
                                </Title>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div className="commission-conditions">
                            <InfoCircleOutlined style={{ color: '#faad14' }} />
                            <Text type="secondary">
                                {settings?.commissionConditions || 'Les commissions sont payées selon les conditions définies par TIGOON Connect.'}
                            </Text>
                        </div>
                    </Col>
                </Row>
            </Card>

            <Card
                className="data-card"
                title={<><DollarOutlined style={{ color: '#D4A853', marginRight: 8 }} />Historique des commissions</>}
            >
                <Table
                    columns={commissionColumns}
                    dataSource={recentCommissions}
                    rowKey="_id"
                    pagination={{ pageSize: 10, size: 'small' }}
                    locale={{ emptyText: 'Aucune commission' }}
                    size="small"
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </>
    );

    // Milestone configuration - Nouveau chauffeur
    const milestoneConfigNew = [
        { key: 'rides5', label: `${milestoneThresholds.milestoneThreshold1} courses`, rides: milestoneThresholds.milestoneThreshold1, amount: commissionRates.rides5 },
        { key: 'rides15', label: `${milestoneThresholds.milestoneThreshold2} courses`, rides: milestoneThresholds.milestoneThreshold2, amount: commissionRates.rides15 },
        { key: 'rides35', label: `${milestoneThresholds.milestoneThreshold3} courses`, rides: milestoneThresholds.milestoneThreshold3, amount: commissionRates.rides35 },
        { key: 'rides55', label: `${milestoneThresholds.milestoneThreshold4} courses`, rides: milestoneThresholds.milestoneThreshold4, amount: commissionRates.rides55 }
    ];

    // Milestone configuration - Réactivation
    const milestoneConfigReactivation = [
        { key: 'rides20Reactivation', label: '20 courses', rides: 20, amount: commissionRates.rides20Reactivation },
        { key: 'hours130Reactivation', label: `${hoursThreshold} heures`, rides: 0, hours: hoursThreshold, amount: commissionRates.hours130Reactivation }
    ];

    // Pour la compatibilité, garder milestoneConfig comme alias de milestoneConfigNew
    const milestoneConfig = milestoneConfigNew;

    // Obtenir la configuration des milestones selon le type de chauffeur
    const getMilestoneConfig = (driverType) => {
        return driverType === 'reactivation' ? milestoneConfigReactivation : milestoneConfigNew;
    };

    const getCompletedMilestonesCount = (milestones, driverType) => {
        if (!milestones) return 0;
        let count = 0;
        if (milestones.accountCreation?.achieved) count++;

        if (driverType === 'reactivation') {
            if (milestones.rides20Reactivation?.achieved) count++;
            if (milestones.hours130Reactivation?.achieved) count++;
        } else {
            if (milestones.rides5?.achieved) count++;
            if (milestones.rides15?.achieved) count++;
            if (milestones.rides35?.achieved) count++;
            if (milestones.rides55?.achieved) count++;
        }
        return count;
    };

    // Calculer le revenu total selon le type de chauffeur
    const calculateDriverRevenue = (milestones, driverType) => {
        if (!milestones) return 0;
        let total = 0;
        if (milestones.accountCreation?.achieved) total += commissionRates.accountCreation;

        if (driverType === 'reactivation') {
            if (milestones.rides20Reactivation?.achieved) total += commissionRates.rides20Reactivation;
            if (milestones.hours130Reactivation?.achieved) total += commissionRates.hours130Reactivation;
        } else {
            if (milestones.rides5?.achieved) total += commissionRates.rides5;
            if (milestones.rides15?.achieved) total += commissionRates.rides15;
            if (milestones.rides35?.achieved) total += commissionRates.rides35;
            if (milestones.rides55?.achieved) total += commissionRates.rides55;
        }
        return total;
    };

    // Obtenir le nombre total de paliers selon le type
    const getTotalMilestonesCount = (driverType) => {
        return driverType === 'reactivation' ? 3 : 5; // Compte + paliers
    };

    const renderPendingRevenuesContent = () => {
        // Déterminer le statut visuel de chaque chauffeur pour le tri
        const getDriverSortStatus = (d) => {
            const completed = getCompletedMilestonesCount(d.milestones, d.driverType);
            const total = getTotalMilestonesCount(d.driverType);
            if (completed >= total) return 1; // Complété - deuxième
            if (d.status === 'expired') return 2; // Expiré - troisième
            if (d.activationWindowEnd && dayjs(d.activationWindowEnd).isBefore(dayjs())) return 2; // Expiré
            return 0; // Actif (temps restant) - premier
        };

        // Trier : actifs d'abord, puis complétés, puis expirés, chacun par courses décroissantes
        const sortedDrivers = [...driversProgress].sort((a, b) => {
            const statusA = getDriverSortStatus(a);
            const statusB = getDriverSortStatus(b);
            if (statusA !== statusB) return statusA - statusB;
            return (b.currentStats?.totalRides || 0) - (a.currentStats?.totalRides || 0);
        });

        const totalDrivers = driversProgress.length;
        // Calculer le total des milestones en tenant compte du type de chauffeur
        const totalMilestones = driversProgress.reduce((acc, record) => {
            return acc + getTotalMilestonesCount(record.driverType);
        }, 0);
        const completedMilestones = driversProgress.reduce((acc, record) => {
            return acc + getCompletedMilestonesCount(record.milestones, record.driverType);
        }, 0);

        return (
            <div className="revenues-page">
                {/* Header Stats */}
                <div className="revenues-header">
                    <Row gutter={[16, 16]}>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{totalDrivers}</div>
                                <div className="revenue-stat-label">Chauffeurs en suivi</div>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{commissionsData?.pendingAmount || 0} F</div>
                                <div className="revenue-stat-label">Revenus en attente</div>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{completedMilestones}/{totalMilestones}</div>
                                <div className="revenue-stat-label">Paliers atteints</div>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{commissionsData?.pendingValidation || 0} F</div>
                                <div className="revenue-stat-label">En validation (48h)</div>
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Drivers Progress Table */}
                <Card
                    title="Chauffeurs en progression"
                    className="drivers-progress-card"
                    extra={
                        <Input
                            placeholder="Rechercher un chauffeur..."
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            value={driverSearchTerm}
                            onChange={(e) => setDriverSearchTerm(e.target.value)}
                            style={{ width: 250 }}
                            allowClear
                        />
                    }
                >
                    {loadingRevenues ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            columns={[
                                {
                                    title: 'Chauffeur',
                                    key: 'driver',
                                    render: (_, record) => {
                                        const rides = record.currentStats?.totalRides || 0;
                                        const isReactivation = record.driverType === 'reactivation';
                                        const milestones = record.milestones || {};
                                        const config = getMilestoneConfig(record.driverType);
                                        const totalRevenue = calculateDriverRevenue(record.milestones, record.driverType);

                                        // Calcul du temps restant
                                        let remainingTag = null;
                                        if (record.activationWindowEnd) {
                                            const now = dayjs();
                                            const end = dayjs(record.activationWindowEnd);
                                            const completedCount = getCompletedMilestonesCount(record.milestones, record.driverType);
                                            const totalCount = getTotalMilestonesCount(record.driverType);
                                            if (completedCount >= totalCount) {
                                                remainingTag = <Tag color="green" style={{ fontSize: 10, margin: 0 }}>Complété</Tag>;
                                            } else if (now.isAfter(end)) {
                                                remainingTag = <Tag color="red" style={{ fontSize: 10, margin: 0 }}>Expiré</Tag>;
                                            } else {
                                                const diffDays = end.diff(now, 'day');
                                                const diffHours = end.diff(now, 'hour') % 24;
                                                remainingTag = (
                                                    <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                                                        <ClockCircleOutlined style={{ marginRight: 2 }} />
                                                        {diffDays > 0 ? `${diffDays}j ${diffHours}h` : `${diffHours}h`}
                                                    </Tag>
                                                );
                                            }
                                        }

                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div style={{ fontWeight: 500, fontSize: 13 }}>
                                                            {record.driverId?.firstName} {record.driverId?.lastName}
                                                        </div>
                                                        <span className="mobile-only-gains" style={{ fontWeight: 600, color: '#D4A853', fontSize: 13, whiteSpace: 'nowrap' }}>
                                                            {totalRevenue} F
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: 11, color: '#888' }}>{formatPhone(record.driverId?.phone)}</div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                                                        <Tag
                                                            color={rides > 0 ? 'blue' : 'default'}
                                                            style={{ fontSize: 11, margin: 0, fontWeight: 600 }}
                                                        >
                                                            {rides} course{rides !== 1 ? 's' : ''}
                                                        </Tag>
                                                        {remainingTag}
                                                        {isReactivation && (
                                                            <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>Réactiv.</Tag>
                                                        )}
                                                    </div>
                                                    <div className="milestone-progress-inline" style={{ marginTop: 4 }}>
                                                        <div className={`milestone-dot ${milestones.accountCreation?.achieved ? 'completed' : ''}`} style={{ width: 18, height: 18, fontSize: 8 }}>
                                                            <CheckCircleOutlined />
                                                        </div>
                                                        {config.map((m) => (
                                                            <div
                                                                key={m.key}
                                                                className={`milestone-dot ${milestones[m.key]?.achieved ? 'completed' : ''}`}
                                                                style={{ width: 18, height: 18, fontSize: 8 }}
                                                            >
                                                                {m.rides || m.hours}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                },
                                {
                                    title: 'Courses',
                                    key: 'rides',
                                    width: 75,
                                    responsive: ['md'],
                                    sorter: (a, b) => (a.currentStats?.totalRides || 0) - (b.currentStats?.totalRides || 0),
                                    defaultSortOrder: 'descend',
                                    render: (_, record) => {
                                        const rides = record.currentStats?.totalRides || 0;
                                        return (
                                            <span style={{ fontWeight: 600, color: rides > 0 ? '#1890ff' : '#8c8c8c' }}>
                                                {rides}
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: 'Progression',
                                    key: 'milestones',
                                    responsive: ['md'],
                                    render: (_, record) => {
                                        const milestones = record.milestones || {};
                                        const isReactivation = record.driverType === 'reactivation';
                                        const config = getMilestoneConfig(record.driverType);

                                        return (
                                            <div className="milestone-progress-inline">
                                                <div className={`milestone-dot ${milestones.accountCreation?.achieved ? 'completed' : ''}`}>
                                                    <CheckCircleOutlined />
                                                </div>
                                                {config.map((m) => (
                                                    <div
                                                        key={m.key}
                                                        className={`milestone-dot ${milestones[m.key]?.achieved ? 'completed' : ''}`}
                                                    >
                                                        {m.rides || m.hours}
                                                    </div>
                                                ))}
                                                {isReactivation && (
                                                    <Tag color="orange" style={{ marginLeft: 8, fontSize: 10 }}>Réactiv.</Tag>
                                                )}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    title: 'Restant',
                                    key: 'remaining',
                                    width: 80,
                                    responsive: ['lg'],
                                    render: (_, record) => {
                                        if (!record.activationWindowEnd) return <span style={{ color: '#bfbfbf' }}>-</span>;
                                        const now = dayjs();
                                        const end = dayjs(record.activationWindowEnd);
                                        const completed = getCompletedMilestonesCount(record.milestones, record.driverType);
                                        const total = getTotalMilestonesCount(record.driverType);
                                        if (completed >= total) {
                                            return <Tag color="green" style={{ fontSize: 11 }}>Complété</Tag>;
                                        }
                                        if (now.isAfter(end)) {
                                            return <Tag color="red" style={{ fontSize: 11 }}>Expiré</Tag>;
                                        }
                                        const diffDays = end.diff(now, 'day');
                                        const diffHours = end.diff(now, 'hour') % 24;
                                        return (
                                            <Tag color="blue" style={{ fontSize: 11 }}>
                                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                {diffDays > 0 ? `${diffDays}j ${diffHours}h` : `${diffHours}h`}
                                            </Tag>
                                        );
                                    }
                                },
                                {
                                    title: 'Gains',
                                    key: 'revenue',
                                    width: 70,
                                    responsive: ['md'],
                                    render: (_, record) => {
                                        const totalRevenue = calculateDriverRevenue(record.milestones, record.driverType);
                                        return (
                                            <span style={{ fontWeight: 600, color: '#D4A853' }}>
                                                {totalRevenue} F
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: '',
                                    key: 'action',
                                    width: 36,
                                    responsive: ['md'],
                                    render: (_, record) => (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => {
                                                setSelectedDriverProgress(record);
                                                setSelectedMenu('driver-progress');
                                                loadDriverProgress(record.driverId?._id || record._id);
                                            }}
                                            style={{ color: '#1890ff', padding: 0 }}
                                        />
                                    )
                                }
                            ]}
                            dataSource={sortedDrivers
                                .filter(d => {
                                    if (!driverSearchTerm) return true;
                                    const search = driverSearchTerm.toLowerCase();
                                    const firstName = (d.driverId?.firstName || '').toLowerCase();
                                    const lastName = (d.driverId?.lastName || '').toLowerCase();
                                    const phone = (d.driverId?.phone || '').toLowerCase();
                                    return firstName.includes(search) || lastName.includes(search) || phone.includes(search);
                                })
                            }
                            rowKey="_id"
                            onRow={(record) => ({
                                onClick: () => {
                                    setSelectedDriverProgress(record);
                                    setSelectedMenu('driver-progress');
                                    loadDriverProgress(record.driverId?._id || record._id);
                                },
                                style: { cursor: 'pointer' }
                            })}
                            pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '30', '50', '100'],
                                showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} chauffeurs`
                            }}
                            locale={{ emptyText: driverSearchTerm ? 'Aucun résultat trouvé' : 'Aucun chauffeur en suivi' }}
                            scroll={{ x: 450 }}
                            size="small"
                        />
                    )}
                </Card>
            </div>
        );
    };

    const renderCollectedRevenuesContent = () => {
        // Grouper les commissions par chauffeur
        const driverMap = {};
        collectedRevenues.forEach(c => {
            const driverId = c.driver?._id || c.driver?.id;
            if (!driverId) return;

            if (!driverMap[driverId]) {
                driverMap[driverId] = {
                    _id: driverId,
                    driver: c.driver,
                    commissions: [],
                    totalAmount: 0,
                    driverType: c.driverType || c.driver?.driverType || 'new',
                    milestoneThresholds: c.milestoneThresholds || null,
                    milestones: {
                        accountCreation: false,
                        rides5: false,
                        rides15: false,
                        rides35: false,
                        rides55: false,
                        rides20Reactivation: false,
                        hours130: false
                    },
                    paidAt: null,
                    paymentReference: null
                };
            }

            // Mettre à jour driverType si disponible
            if (c.driverType || c.driver?.driverType) {
                driverMap[driverId].driverType = c.driverType || c.driver?.driverType;
            }

            driverMap[driverId].commissions.push(c);
            driverMap[driverId].totalAmount += c.amount || 0;

            // Marquer les milestones atteints
            if (c.type === 'account_creation') driverMap[driverId].milestones.accountCreation = true;
            if (c.type === 'activation_rides_5') driverMap[driverId].milestones.rides5 = true;
            if (c.type === 'activation_rides_15') driverMap[driverId].milestones.rides15 = true;
            if (c.type === 'activation_rides_35') driverMap[driverId].milestones.rides35 = true;
            if (c.type === 'activation_rides_55') driverMap[driverId].milestones.rides55 = true;
            if (c.type === 'reactivation_rides_20') driverMap[driverId].milestones.rides20Reactivation = true;
            if (c.type === 'activation_hours_130' || c.type === 'reactivation_hours_130') driverMap[driverId].milestones.hours130 = true;

            // Garder la date de paiement la plus récente
            if (c.paidAt && (!driverMap[driverId].paidAt || new Date(c.paidAt) > new Date(driverMap[driverId].paidAt))) {
                driverMap[driverId].paidAt = c.paidAt;
                driverMap[driverId].paymentReference = c.paymentReference;
            }
        });

        const driversWithPayments = Object.values(driverMap).sort((a, b) =>
            new Date(b.paidAt || 0) - new Date(a.paidAt || 0)
        );

        const totalCollected = collectedRevenues.reduce((acc, c) => acc + (c.amount || 0), 0);
        const uniqueDrivers = driversWithPayments.length;

        // Déterminer si c'est un compte réactivé basé sur driverType
        const isReactivation = (record) => record.driverType === 'reactivation';

        const columns = [
            {
                title: 'Chauffeur',
                key: 'driver',
                width: 250,
                render: (_, record) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar
                            size={40}
                            icon={<UserOutlined />}
                            style={{
                                backgroundColor: '#D4A853',
                                flexShrink: 0
                            }}
                        />
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {record.driver?.firstName} {record.driver?.lastName}
                            </div>
                            <div style={{ color: '#888', fontSize: 12 }}>
                                {formatPhone(record.driver?.phone)}
                            </div>
                            {record.driverType === 'reactivation' && (
                                <Tag color="orange" style={{ fontSize: 10, borderRadius: 8, marginTop: 2 }}>
                                    Réactiv.
                                </Tag>
                            )}
                        </div>
                    </div>
                )
            },
            {
                title: 'Progression',
                key: 'progression',
                width: 280,
                render: (_, record) => {
                    const isReact = isReactivation(record);
                    const milestones = record.milestones;
                    const dt = record.milestoneThresholds || milestoneThresholds;

                    if (isReact) {
                        // Chauffeur réactivé: Compte, 20 courses, 130h
                        return (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Tag color={milestones.accountCreation ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                    {milestones.accountCreation && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                    Compte
                                </Tag>
                                <Tag color={milestones.rides20Reactivation ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                    {milestones.rides20Reactivation && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                    20
                                </Tag>
                                <Tag color={milestones.hours130 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                    {milestones.hours130 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                    {hoursThreshold}h
                                </Tag>
                            </div>
                        );
                    }

                    // Nouveau chauffeur: Compte + paliers dynamiques (ville du chauffeur)
                    return (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Tag color={milestones.accountCreation ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.accountCreation && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                Compte
                            </Tag>
                            <Tag color={milestones.rides5 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides5 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold1 || 5}
                            </Tag>
                            <Tag color={milestones.rides15 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides15 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold2 || 15}
                            </Tag>
                            <Tag color={milestones.rides35 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides35 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold3 || 35}
                            </Tag>
                            <Tag color={milestones.rides55 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides55 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold4 || 55}
                            </Tag>
                        </div>
                    );
                }
            },
            {
                title: 'Gains',
                key: 'gains',
                width: 100,
                align: 'center',
                sorter: (a, b) => a.totalAmount - b.totalAmount,
                render: (_, record) => (
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#52c41a' }}>
                        {record.totalAmount.toLocaleString()} F
                    </span>
                )
            },
            {
                title: 'Paiement',
                key: 'payment',
                width: 100,
                align: 'center',
                render: (_, record) => (
                    <Tag color="green" style={{ borderRadius: 10 }}>
                        <CheckCircleOutlined style={{ marginRight: 4 }} />
                        Payé
                    </Tag>
                )
            },
            {
                title: 'Date paiement',
                key: 'paidAt',
                width: 120,
                render: (_, record) => (
                    <div style={{ fontSize: 12, color: '#666' }}>
                        {record.paidAt ? new Date(record.paidAt).toLocaleDateString('fr-FR') : '-'}
                    </div>
                )
            },
            {
                title: 'Référence',
                key: 'reference',
                width: 120,
                render: (_, record) => (
                    <div style={{ fontSize: 12, color: '#888' }}>
                        {record.paymentReference || '-'}
                    </div>
                )
            }
        ];

        return (
            <div className="revenues-page">
                {/* Header Stats */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={8}>
                        <Card>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <WalletOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                                <div>
                                    <div style={{ color: '#888', fontSize: 12 }}>Total encaissé</div>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{totalCollected.toLocaleString()} F</div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <TeamOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                                <div>
                                    <div style={{ color: '#888', fontSize: 12 }}>Chauffeurs payés</div>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>{uniqueDrivers}</div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                                <div>
                                    <div style={{ color: '#888', fontSize: 12 }}>Commissions payées</div>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{collectedRevenues.length}</div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Liste des chauffeurs payés */}
                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <span>Chauffeurs payés</span>
                            <Tag style={{ marginLeft: 8, borderRadius: 10 }}>{uniqueDrivers}</Tag>
                        </div>
                    }
                >
                    {loadingRevenues ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={driversWithPayments}
                            rowKey="_id"
                            pagination={{
                                showSizeChanger: true,
                                showTotal: (total) => `${total} chauffeur(s) payé(s)`,
                                defaultPageSize: 10
                            }}
                            scroll={{ x: 900 }}
                            size="middle"
                            locale={{ emptyText: 'Aucun chauffeur payé pour le moment' }}
                        />
                    )}
                </Card>
            </div>
        );
    };

    const renderRevenues130hContent = () => {
        // Format period label (YYYY-MM -> "Mars 2026")
        const formatPeriodLabel = (period) => {
            if (!period) return '-';
            const d = dayjs(period + '-01');
            return d.isValid() ? d.format('MMMM YYYY').replace(/^\w/, c => c.toUpperCase()) : period;
        };

        const currentPeriod = dayjs().format('YYYY-MM');

        // Group records by period
        const recordsByPeriod = {};
        revenues130h.forEach(r => {
            const period = r.period || 'unknown';
            if (!recordsByPeriod[period]) {
                recordsByPeriod[period] = [];
            }
            recordsByPeriod[period].push(r);
        });

        // Sort periods descending
        const sortedPeriods = Object.keys(recordsByPeriod).sort((a, b) => b.localeCompare(a));

        // Determine payment status for a period
        const getPeriodPaymentStatus = (period, periodStat) => {
            const isCurrentPeriod = period === currentPeriod;
            if (isCurrentPeriod) return { label: 'En cours', color: 'orange', icon: <ClockCircleOutlined /> };
            const paidAmount = periodStat?.paidAmount || 0;
            const totalCommission = periodStat?.totalCommission || 0;
            if (totalCommission === 0) return { label: 'Aucun objectif', color: 'default', icon: <ClockCircleOutlined /> };
            if (paidAmount > 0 && paidAmount >= totalCommission) return { label: 'Payé', color: 'green', icon: <CheckCircleOutlined /> };
            if (paidAmount > 0) return { label: 'Partiel', color: 'blue', icon: <ClockCircleOutlined /> };
            return { label: 'En attente', color: 'orange', icon: <ClockCircleOutlined /> };
        };

        return (
            <div className="revenues-page">
                {/* Info */}
                <Card
                    style={{ marginBottom: 16, background: '#fff7e6', border: '1px solid #ffd591' }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ClockCircleOutlined style={{ fontSize: 18, color: '#fa8c16' }} />
                        <div>
                            <Text strong style={{ color: '#d46b08' }}>
                                Commission {hoursThreshold}h : {commissionRates.hours130} F / mois / chauffeur
                            </Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                Versée chaque mois pendant 3 mois pour les chauffeurs qui atteignent {hoursThreshold} heures de conduite.
                            </Text>
                        </div>
                    </div>
                </Card>

                {/* Periods breakdown */}
                {loadingRevenues ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                    </div>
                ) : sortedPeriods.length === 0 ? (
                    <Card>
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <CalendarOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <div>
                                <Text type="secondary">Aucune donnée {hoursThreshold}h pour le moment</Text>
                            </div>
                        </div>
                    </Card>
                ) : (
                    sortedPeriods.map(period => {
                        const periodDrivers = (recordsByPeriod[period] || []).sort((a, b) => (b.hoursWorked || 0) - (a.hoursWorked || 0));
                        const periodStat = revenues130hPeriodStats.find(p => p._id === period);
                        const isCurrentPeriod = period === currentPeriod;
                        const periodTotalHours = periodStat?.totalHours || periodDrivers.reduce((sum, d) => sum + (d.hoursWorked || 0), 0);
                        const periodTotalCommission = periodStat?.totalCommission || periodDrivers.reduce((sum, d) => sum + (d.affiliateCommission || 0), 0);
                        const periodDriversCount = periodStat?.driversCount || periodDrivers.length;
                        const periodDriversReached = periodStat?.driversReached || periodDrivers.filter(d => d.reachedThreshold).length;
                        const periodPaidAmount = periodStat?.paidAmount || 0;
                        const paymentStatus = getPeriodPaymentStatus(period, periodStat);

                        return (
                            <Card
                                key={period}
                                style={{ marginBottom: 16 }}
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <CalendarOutlined />
                                        <span>{formatPeriodLabel(period)}</span>
                                        <Tag color={paymentStatus.color} icon={paymentStatus.icon} style={{ marginLeft: 4 }}>
                                            {paymentStatus.label}
                                        </Tag>
                                        {periodTotalCommission > 0 && (
                                            <Tag color="blue">
                                                {Math.round(periodTotalCommission).toLocaleString()} F
                                            </Tag>
                                        )}
                                    </div>
                                }
                                extra={
                                    <div style={{ textAlign: 'right' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Heures: <strong>{Math.round(periodTotalHours)}h</strong> | {periodDriversCount} chauffeur{periodDriversCount > 1 ? 's' : ''}
                                            {periodDriversReached > 0 && (
                                                <span style={{ color: '#52c41a', marginLeft: 8 }}>
                                                    {periodDriversReached} x {hoursThreshold}h
                                                </span>
                                            )}
                                        </Text>
                                        {!isCurrentPeriod && periodPaidAmount > 0 && (
                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                <Text style={{ color: '#52c41a' }}>Payé: {Math.round(periodPaidAmount).toLocaleString()} F</Text>
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <Table
                                    columns={[
                                        {
                                            title: 'Chauffeur',
                                            key: 'driver',
                                            render: (_, record) => {
                                                const driver = record.driverId || record;
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {driver?.firstName || ''} {driver?.lastName || ''}
                                                            </div>
                                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                                {formatPhone(driver?.phone)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Heures',
                                            key: 'hours',
                                            align: 'center',
                                            render: (_, record) => {
                                                const hours = record.hoursWorked || 0;
                                                const reached = hours >= hoursThreshold;
                                                return (
                                                    <div>
                                                        <span style={{
                                                            fontWeight: 700,
                                                            color: reached ? '#52c41a' : hours > 0 ? '#fa8c16' : '#bfbfbf'
                                                        }}>
                                                            {Math.round(hours * 10) / 10}h
                                                        </span>
                                                        <span style={{ fontSize: 11, color: '#888' }}> / {hoursThreshold}h</span>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Progression',
                                            key: 'progress',
                                            width: 130,
                                            responsive: ['md'],
                                            render: (_, record) => {
                                                const hours = record.hoursWorked || 0;
                                                const percent = Math.min(100, Math.round((hours / hoursThreshold) * 100));
                                                return (
                                                    <div style={{ width: 100 }}>
                                                        <div style={{
                                                            background: '#f0f0f0',
                                                            borderRadius: 10,
                                                            height: 8,
                                                            overflow: 'hidden'
                                                        }}>
                                                            <div style={{
                                                                width: `${percent}%`,
                                                                background: percent >= 100 ? '#52c41a' : '#fa8c16',
                                                                height: '100%',
                                                                borderRadius: 10,
                                                                transition: 'width 0.3s'
                                                            }} />
                                                        </div>
                                                        {record.reachedThreshold && (
                                                            <div style={{ fontSize: 10, color: '#52c41a', marginTop: 2 }}>
                                                                <CheckCircleOutlined style={{ marginRight: 2 }} />Objectif atteint
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Statut',
                                            key: 'status',
                                            align: 'center',
                                            render: (_, record) => {
                                                if (record.paymentStatus === 'paid') {
                                                    return <Tag color="green" icon={<CheckCircleOutlined />}>Payé</Tag>;
                                                }
                                                if (record.paymentStatus === 'pending') {
                                                    return <Tag color="gold" icon={<ClockCircleOutlined />}>En attente</Tag>;
                                                }
                                                return <Tag color="default" icon={<ClockCircleOutlined />}>En cours</Tag>;
                                            }
                                        },
                                        {
                                            title: 'Commission',
                                            key: 'commission',
                                            align: 'right',
                                            render: (_, record) => (
                                                <span style={{
                                                    fontWeight: 600,
                                                    color: record.reachedThreshold ? '#52c41a' : '#bfbfbf'
                                                }}>
                                                    {Math.round(record.affiliateCommission || 0).toLocaleString()} F
                                                </span>
                                            )
                                        }
                                    ]}
                                    dataSource={periodDrivers}
                                    rowKey="_id"
                                    pagination={periodDrivers.length > 10 ? {
                                        defaultPageSize: 10,
                                        showSizeChanger: true,
                                        showTotal: (total) => `${total} chauffeur${total > 1 ? 's' : ''}`
                                    } : false}
                                    scroll={{ x: 500 }}
                                    size="small"
                                />
                            </Card>
                        );
                    })
                )}
            </div>
        );
    };

    const renderBonusRechargeContent = () => {
        // Calculer les statistiques
        const totalBonuses = bonusRecharges.length;
        const totalAmount = bonusRecharges.reduce((sum, b) => sum + (b.bonusAmount || 0), 0);
        const paidBonuses = bonusRecharges.filter(b => b.isPaid);
        const paidAmount = paidBonuses.reduce((sum, b) => sum + (b.bonusAmount || 0), 0);
        const pendingAmount = totalAmount - paidAmount;

        return (
            <div className="revenues-page">
                {/* Description */}
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Bonus de 2 000 F reçu lorsqu'au moins 2 chauffeurs recrutés le même jour rechargent chacun au moins 2 000 F
                </Text>

                {/* Stats Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} md={8}>
                        <div className="revenue-stat-card">
                            <div className="revenue-stat-value">{totalBonuses}</div>
                            <div className="revenue-stat-label">Total bonus</div>
                        </div>
                    </Col>
                    <Col xs={12} md={8}>
                        <div className="revenue-stat-card">
                            <div className="revenue-stat-value" style={{ color: '#52c41a' }}>{paidAmount.toLocaleString()} F</div>
                            <div className="revenue-stat-label">Payé</div>
                        </div>
                    </Col>
                    <Col xs={12} md={8}>
                        <div className="revenue-stat-card">
                            <div className="revenue-stat-value" style={{ color: '#fa8c16' }}>{pendingAmount.toLocaleString()} F</div>
                            <div className="revenue-stat-label">En attente</div>
                        </div>
                    </Col>
                </Row>

                {/* Info */}
                <Card
                    style={{ marginBottom: 16, background: '#e6f7ff', border: '1px solid #91d5ff' }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ThunderboltOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                        <div>
                            <Text strong style={{ color: '#096dd9' }}>
                                Bonus Recharge Même Jour - 2 000 F
                            </Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                Vous recevez un bonus de 2 000 F lorsqu'au moins 2 chauffeurs que vous avez recrutés le même jour effectuent chacun une recharge d'au moins 2 000 F.
                            </Text>
                        </div>
                    </div>
                </Card>

                {/* Bonus List */}
                <Card title="Historique des bonus">
                    {loadingBonusRecharge ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            columns={[
                                {
                                    title: 'Date',
                                    key: 'date',
                                    render: (_, record) => (
                                        <div>
                                            <div style={{ fontWeight: 500 }}>
                                                {dayjs(record.date).format('DD MMM YYYY')}
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    title: 'Chauffeurs',
                                    key: 'drivers',
                                    render: (_, record) => {
                                        const drivers = record.qualifyingDrivers || [];
                                        return (
                                            <div>
                                                {drivers.map((d, i) => (
                                                    <Tag key={i} color="blue" style={{ marginBottom: 4 }}>
                                                        {d.driverId?.firstName || ''} {d.driverId?.lastName || ''}
                                                    </Tag>
                                                ))}
                                                {drivers.length === 0 && <Text type="secondary">-</Text>}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    title: 'Montant',
                                    key: 'amount',
                                    align: 'right',
                                    render: (_, record) => (
                                        <span style={{ fontWeight: 600, color: '#52c41a' }}>
                                            {(record.bonusAmount || 0).toLocaleString()} F
                                        </span>
                                    )
                                },
                                {
                                    title: 'Statut',
                                    key: 'status',
                                    align: 'center',
                                    render: (_, record) => (
                                        record.isPaid
                                            ? <Tag color="green" icon={<CheckCircleOutlined />}>Payé</Tag>
                                            : <Tag color="gold" icon={<ClockCircleOutlined />}>En attente</Tag>
                                    )
                                }
                            ]}
                            dataSource={bonusRecharges}
                            rowKey="_id"
                            pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total) => `${total} bonus`
                            }}
                            locale={{ emptyText: 'Aucun bonus recharge pour le moment' }}
                            scroll={{ x: 500 }}
                            size="small"
                        />
                    )}
                </Card>
            </div>
        );
    };

    const renderMonthlyRetentionsContent = () => {
        // Format period label (YYYY-MM -> "Mars 2026")
        const formatPeriodLabel = (period) => {
            if (!period) return '-';
            const d = dayjs(period + '-01');
            return d.isValid() ? d.format('MMMM YYYY').replace(/^\w/, c => c.toUpperCase()) : period;
        };

        // Get current period (YYYY-MM)
        const currentPeriod = dayjs().format('YYYY-MM');

        // Compute overall stats from periodStats
        const totalRetention = monthlyRetentionsPeriodStats.reduce((sum, p) => sum + (p.totalCommission || 0), 0);
        const totalPaid = monthlyRetentionsPeriodStats.reduce((sum, p) => sum + (p.paidAmount || 0), 0);
        const totalPending = totalRetention - totalPaid;
        const totalDrivers = monthlyRetentionsPeriodStats.reduce((sum, p) => sum + (p.driversCount || 0), 0);

        // Group retentions by period
        const retentionsByPeriod = {};
        monthlyRetentions.forEach(r => {
            const period = r.period || 'unknown';
            if (!retentionsByPeriod[period]) {
                retentionsByPeriod[period] = [];
            }
            retentionsByPeriod[period].push(r);
        });

        // Sort periods descending
        const sortedPeriods = Object.keys(retentionsByPeriod).sort((a, b) => b.localeCompare(a));

        // Determine payment status for a period
        const getPeriodPaymentStatus = (period, periodStat) => {
            const isCurrentPeriod = period === currentPeriod;
            if (isCurrentPeriod) return { label: 'En cours', color: 'orange', icon: <ClockCircleOutlined /> };
            const paidAmount = periodStat?.paidAmount || 0;
            const totalCommission = periodStat?.totalCommission || 0;
            if (paidAmount > 0 && paidAmount >= totalCommission) return { label: 'Payé', color: 'green', icon: <CheckCircleOutlined /> };
            if (paidAmount > 0) return { label: 'Partiel', color: 'blue', icon: <ClockCircleOutlined /> };
            return { label: 'En attente', color: 'orange', icon: <ClockCircleOutlined /> };
        };

        return (
            <div className="revenues-page">
                {/* Info */}
                <Card
                    style={{ marginBottom: 16, background: '#e6f7ff', border: '1px solid #91d5ff' }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <CalendarOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                        <div>
                            <Text strong style={{ color: '#096dd9' }}>
                                Rétention Mensuelle - 1%
                            </Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                Chaque mois, vous recevez 1% du total des recharges (Wave + Orange Money) effectuées par vos chauffeurs recrutés.
                            </Text>
                        </div>
                    </div>
                </Card>

                {/* Periods breakdown */}
                {loadingMonthlyRetentions ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                    </div>
                ) : sortedPeriods.length === 0 ? (
                    <Card>
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <CalendarOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <div>
                                <Text type="secondary">Aucune rétention mensuelle pour le moment</Text>
                            </div>
                        </div>
                    </Card>
                ) : (
                    sortedPeriods.map(period => {
                        const periodDrivers = retentionsByPeriod[period] || [];
                        const periodStat = monthlyRetentionsPeriodStats.find(p => p._id === period);
                        const isCurrentPeriod = period === currentPeriod;
                        const periodTotalRecharges = periodStat?.totalRecharges || periodDrivers.reduce((sum, d) => sum + (d.totalRecharges || 0), 0);
                        const periodTotalCommission = periodStat?.totalCommission || periodDrivers.reduce((sum, d) => sum + (d.affiliateCommission || 0), 0);
                        const periodDriversCount = periodStat?.driversCount || periodDrivers.length;
                        const periodPaidAmount = periodStat?.paidAmount || 0;
                        const periodRemainingAmount = Math.max(0, periodTotalCommission - periodPaidAmount);
                        const isPaid = paymentStatus => paymentStatus.label === 'Payé';
                        const paymentStatus = getPeriodPaymentStatus(period, periodStat);

                        return (
                            <Card
                                key={period}
                                style={{ marginBottom: 16 }}
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <CalendarOutlined />
                                        <span>{formatPeriodLabel(period)}</span>
                                        <Tag color={paymentStatus.color} icon={paymentStatus.icon} style={{ marginLeft: 4 }}>
                                            {paymentStatus.label}
                                        </Tag>
                                        <Tag color="blue">
                                            {Math.round(periodTotalCommission).toLocaleString()} F
                                        </Tag>
                                    </div>
                                }
                                extra={
                                    <div style={{ textAlign: 'right' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Recharges: <strong>{Math.round(periodTotalRecharges).toLocaleString()} F</strong> | {periodDriversCount} chauffeur{periodDriversCount > 1 ? 's' : ''}
                                        </Text>
                                        {!isCurrentPeriod && periodRemainingAmount > 0 && (
                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                <Text style={{ color: '#52c41a' }}>Payé: {Math.round(periodPaidAmount).toLocaleString()} F</Text>
                                                <Text style={{ color: '#fa8c16', marginLeft: 8 }}>Reste: {Math.round(periodRemainingAmount).toLocaleString()} F</Text>
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <Table
                                    columns={[
                                        {
                                            title: 'Chauffeur',
                                            key: 'driver',
                                            render: (_, record) => {
                                                const driver = record.driverId;
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {driver?.firstName || ''} {driver?.lastName || ''}
                                                            </div>
                                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                                {formatPhone(driver?.phone)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Recharges',
                                            key: 'recharges',
                                            align: 'right',
                                            render: (_, record) => (
                                                <span style={{ fontWeight: 600, color: '#1890ff' }}>
                                                    {(record.totalRecharges || 0).toLocaleString()} F
                                                </span>
                                            )
                                        },
                                        {
                                            title: 'Wave',
                                            key: 'wave',
                                            align: 'right',
                                            responsive: ['md'],
                                            render: (_, record) => (
                                                <span>{(record.waveRecharges || 0).toLocaleString()} F</span>
                                            )
                                        },
                                        {
                                            title: 'OM',
                                            key: 'om',
                                            align: 'right',
                                            responsive: ['md'],
                                            render: (_, record) => (
                                                <span>{(record.orangeMoneyRecharges || 0).toLocaleString()} F</span>
                                            )
                                        },
                                        {
                                            title: 'Commission',
                                            key: 'commission',
                                            align: 'right',
                                            render: (_, record) => (
                                                <span style={{ fontWeight: 600, color: '#52c41a' }}>
                                                    {Math.round(record.affiliateCommission || 0).toLocaleString()} F
                                                </span>
                                            )
                                        }
                                    ]}
                                    dataSource={periodDrivers}
                                    rowKey="_id"
                                    pagination={periodDrivers.length > 10 ? {
                                        defaultPageSize: 10,
                                        showSizeChanger: true,
                                        showTotal: (total) => `${total} chauffeur${total > 1 ? 's' : ''}`
                                    } : false}
                                    scroll={{ x: 500 }}
                                    size="small"
                                />
                            </Card>
                        );
                    })
                )}
            </div>
        );
    };

    const renderDriverProgressContent = () => {
        if (!selectedDriverProgress) {
            setSelectedMenu('revenues-pending');
            return null;
        }

        // Gérer les deux structures possibles:
        // 1. Avant chargement API: selectedDriverProgress est le DriverProgress de la liste
        // 2. Après chargement API: selectedDriverProgress est { driver, vehicle, progress, commissions, rates }
        const isDetailLoaded = selectedDriverProgress.progress !== undefined || selectedDriverProgress.driver !== undefined;

        // Extraire le DriverProgress
        const progressData = isDetailLoaded ? selectedDriverProgress.progress : selectedDriverProgress;

        // Extraire les données du chauffeur
        // Priorité: selectedDriverProgress.driver (API) > progressData.driverId (DriverProgress populé)
        const driverInfo = selectedDriverProgress.driver || progressData?.driverId || {};
        const vehicleInfo = selectedDriverProgress.vehicle || null;
        const milestones = progressData?.milestones || {};
        const currentStats = progressData?.currentStats || {};
        const commissions = isDetailLoaded ? (selectedDriverProgress.commissions || []) : [];
        const activationWindowEnd = progressData?.activationWindowEnd;
        const createdAt = driverInfo?.createdAt || progressData?.createdAt;

        // Extraire le type de chauffeur
        const driverType = progressData?.driverType || selectedDriverProgress.driverType || 'new';

        // Calculer le revenu total - priorité au totalRevenue de l'API
        let totalRevenue = selectedDriverProgress.totalRevenue || 0;
        if (!totalRevenue) {
            totalRevenue = calculateDriverRevenue(milestones, driverType);
        }

        return (
            <div className="driver-progress-page">
                {/* Back Button */}
                <div className="detail-page-header">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            setSelectedDriverProgress(null);
                            setSelectedMenu('referrals');
                        }}
                        className="back-button"
                    >
                        Retour à mes chauffeurs
                    </Button>
                </div>

                <Row gutter={[24, 24]}>
                    {/* Driver Info */}
                    <Col xs={24} lg={8}>
                        <Card className="driver-info-card">
                            <div className="driver-avatar-section">
                                <Avatar size={80} icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                <Title level={4} style={{ margin: '16px 0 4px' }}>
                                    {driverInfo.firstName} {driverInfo.lastName}
                                </Title>
                                <Text type="secondary">{formatPhone(driverInfo.phone)}</Text>
                            </div>

                            <div className="driver-stats-grid">
                                <div className="driver-stat-item">
                                    <div className="stat-value">{currentStats.totalRides || 0}</div>
                                    <div className="stat-label">Courses</div>
                                </div>
                                <div className="driver-stat-item">
                                    <div className="stat-value">{currentStats.totalHours || 0}h</div>
                                    <div className="stat-label">Heures</div>
                                </div>
                                <div className="driver-stat-item">
                                    <div className="stat-value">{totalRevenue} F</div>
                                    <div className="stat-label">Revenus</div>
                                </div>
                            </div>

                            <div className="driver-info-details">
                                <div className="info-row">
                                    <PhoneOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Téléphone</span>
                                        <span className="info-value">{formatPhone(driverInfo.phone)}</span>
                                    </div>
                                </div>
                                {driverInfo.email && (
                                    <div className="info-row">
                                        <MailOutlined className="info-icon" />
                                        <div className="info-content">
                                            <span className="info-label">Email</span>
                                            <span className="info-value">{driverInfo.email}</span>
                                        </div>
                                    </div>
                                )}
                                {driverInfo.residence && (
                                    <div className="info-row">
                                        <EnvironmentOutlined className="info-icon" />
                                        <div className="info-content">
                                            <span className="info-label">Résidence</span>
                                            <span className="info-value">{driverInfo.residence}</span>
                                        </div>
                                    </div>
                                )}
                                <div className="info-row">
                                    <CalendarOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Date d'inscription</span>
                                        <span className="info-value">
                                            {createdAt ? dayjs(createdAt).format('DD/MM/YYYY') : '-'}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <ClockCircleOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Fenêtre d'activation</span>
                                        <span className="info-value">
                                            {activationWindowEnd
                                                ? dayjs(activationWindowEnd).format('DD/MM/YYYY')
                                                : '7 jours'}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <div className="info-icon" style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: driverInfo.isActive !== false ? '#52c41a' : '#d9d9d9',
                                        marginTop: 6
                                    }} />
                                    <div className="info-content">
                                        <span className="info-label">Statut</span>
                                        <span className="info-value">
                                            <Tag color={driverInfo.isActive !== false ? 'success' : 'default'}>
                                                {driverInfo.isActive !== false ? 'Actif' : 'Inactif'}
                                            </Tag>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Vehicle Info Card */}
                        <Card
                            className="vehicle-info-card"
                            style={{ marginTop: 16 }}
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <CarOutlined style={{ fontSize: 18, color: '#D4A853' }} />
                                    <span>Véhicule</span>
                                </div>
                            }
                        >
                            {vehicleInfo ? (
                                <div className="vehicle-info-content">
                                    <div style={{
                                        background: 'linear-gradient(135deg, #D4A85315 0%, #D4A85325 100%)',
                                        borderRadius: 12,
                                        padding: 16,
                                        marginBottom: 16
                                    }}>
                                        <div style={{ fontWeight: 600, fontSize: 18, color: '#262626', marginBottom: 4 }}>
                                            {vehicleInfo.marque} {vehicleInfo.modele}
                                        </div>
                                        <div style={{ fontSize: 14, color: '#8c8c8c' }}>
                                            {vehicleInfo.annee} • {vehicleInfo.couleur}
                                        </div>
                                    </div>
                                    <Row gutter={[12, 12]}>
                                        <Col span={12}>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Immatriculation</div>
                                            <div style={{ fontWeight: 600, color: '#262626' }}>
                                                {vehicleInfo.immatriculation || '-'}
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Catégorie</div>
                                            <div style={{ fontWeight: 600, color: '#262626' }}>
                                                <Tag color="gold">{vehicleInfo.categorie || 'Standard'}</Tag>
                                            </div>
                                        </Col>
                                        {vehicleInfo.kilometrage && (
                                            <Col span={12}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Kilométrage</div>
                                                <div style={{ fontWeight: 600, color: '#262626' }}>
                                                    {vehicleInfo.kilometrage?.toLocaleString() || '-'} km
                                                </div>
                                            </Col>
                                        )}
                                        <Col span={12}>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Statut véhicule</div>
                                            <div style={{ fontWeight: 600, color: '#262626' }}>
                                                <Tag color={vehicleInfo.status === 'active' ? 'success' : 'default'}>
                                                    {vehicleInfo.status === 'active' ? 'Actif' : vehicleInfo.status || 'Actif'}
                                                </Tag>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '24px 16px', color: '#8c8c8c' }}>
                                    <CarOutlined style={{ fontSize: 36, color: '#d9d9d9', marginBottom: 12 }} />
                                    <div>Aucun véhicule associé</div>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Milestones Timeline */}
                    <Col xs={24} lg={16}>
                        <Card
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span>Progression des paliers</span>
                                    {driverType === 'reactivation' && (
                                        <Tag color="orange">Réactivation</Tag>
                                    )}
                                </div>
                            }
                            className="milestones-detail-card"
                        >
                            {loadingRevenues ? (
                                <div style={{ textAlign: 'center', padding: 40 }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <div className="milestones-timeline-vertical">
                                    {/* Account Creation */}
                                    <div className={`milestone-row ${milestones.accountCreation?.achieved ? 'completed' : 'pending'}`}>
                                        <div className="milestone-icon">
                                            {milestones.accountCreation?.achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                        </div>
                                        <div className="milestone-content">
                                            <div className="milestone-title">Création de compte</div>
                                            <div className="milestone-subtitle">
                                                {milestones.accountCreation?.achieved
                                                    ? `Atteint le ${dayjs(milestones.accountCreation.achievedAt).format('DD/MM/YYYY')}`
                                                    : 'En attente'}
                                            </div>
                                        </div>
                                        <div className="milestone-amount">{commissionRates.accountCreation} F</div>
                                    </div>

                                    {/* Ride Milestones - selon le type de chauffeur */}
                                    {getMilestoneConfig(driverType).map((m) => (
                                        <div
                                            key={m.key}
                                            className={`milestone-row ${milestones[m.key]?.achieved ? 'completed' : 'pending'}`}
                                        >
                                            <div className="milestone-icon">
                                                {milestones[m.key]?.achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                            </div>
                                            <div className="milestone-content">
                                                <div className="milestone-title">{m.label}</div>
                                                <div className="milestone-subtitle">
                                                    {milestones[m.key]?.achieved
                                                        ? `Atteint le ${dayjs(milestones[m.key].achievedAt).format('DD/MM/YYYY')}`
                                                        : m.hours
                                                            ? `${currentStats.totalHours || 0}/${m.hours} heures (mensuel)`
                                                            : `${currentStats.totalRides || 0}/${m.rides} courses`}
                                                </div>
                                            </div>
                                            <div className="milestone-amount">{m.amount} F</div>
                                        </div>
                                    ))}

                                    {/* Hours Milestone - seulement pour les nouveaux chauffeurs */}
                                    {driverType !== 'reactivation' && (
                                        <div className={`milestone-row ${milestones.hours130?.achieved ? 'completed' : 'pending'}`}>
                                            <div className="milestone-icon">
                                                {milestones.hours130?.achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                            </div>
                                            <div className="milestone-content">
                                                <div className="milestone-title">{hoursThreshold} heures</div>
                                                <div className="milestone-subtitle">
                                                    {milestones.hours130?.achieved
                                                        ? `Atteint le ${dayjs(milestones.hours130.achievedAt).format('DD/MM/YYYY')}`
                                                        : `${currentStats.totalHours || 0}/${hoursThreshold} heures (mensuel)`}
                                                </div>
                                            </div>
                                            <div className="milestone-amount">{commissionRates.hours130} F</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>

                        {/* Driver Commissions */}
                        <Card title="Commissions générées" style={{ marginTop: 24 }}>
                            <Table
                                columns={[
                                    {
                                        title: 'Date',
                                        key: 'date',
                                        render: (_, record) => dayjs(record.createdAt).format('DD/MM/YYYY')
                                    },
                                    {
                                        title: 'Type',
                                        key: 'type',
                                        render: (_, record) => record.description || record.type
                                    },
                                    {
                                        title: 'Montant',
                                        key: 'amount',
                                        render: (_, record) => (
                                            <span style={{ fontWeight: 600, color: '#52c41a' }}>+{record.amount} F</span>
                                        )
                                    },
                                    {
                                        title: 'Statut',
                                        key: 'status',
                                        render: (_, record) => {
                                            const colors = {
                                                pending: 'orange',
                                                pending_validation: 'blue',
                                                approved: 'green',
                                                paid: 'green'
                                            };
                                            const labels = {
                                                pending: 'En attente',
                                                pending_validation: 'Validation 48h',
                                                approved: 'Approuvé',
                                                paid: 'Payé'
                                            };
                                            return <Tag color={colors[record.status]}>{labels[record.status]}</Tag>;
                                        }
                                    }
                                ]}
                                dataSource={commissions}
                                rowKey="_id"
                                pagination={false}
                                locale={{ emptyText: 'Aucune commission' }}
                                scroll={{ x: 450 }}
                                size="small"
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    };

    const renderProfileContent = () => (
        <div className="profile-page-content">
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                    <Card title={<><UserOutlined style={{ marginRight: 8 }} />Informations personnelles</>} className="profile-card">
                    <div className="profile-info-list">
                        <div className="info-row">
                            <UserOutlined className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Nom complet</span>
                                <span className="info-value">{affiliate?.firstName} {affiliate?.lastName}</span>
                            </div>
                        </div>
                        <div className="info-row">
                            <PhoneOutlined className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Téléphone</span>
                                <span className="info-value">{formatPhone(affiliate?.phone)}</span>
                            </div>
                        </div>
                        <div className="info-row">
                            <MailOutlined className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Email</span>
                                <span className="info-value">{affiliate?.email || 'Non renseigné'}</span>
                            </div>
                        </div>
                        <div className="info-row">
                            <EnvironmentOutlined className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Ville</span>
                                <span className="info-value">{affiliate?.city || 'Non renseigné'}</span>
                            </div>
                        </div>
                        <div className="info-row">
                            <CalendarOutlined className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Affilié depuis</span>
                                <span className="info-value">{dayjs(affiliate?.approvedAt).format('DD/MM/YYYY')}</span>
                            </div>
                        </div>
                    </div>
                </Card>
            </Col>
            <Col xs={24} lg={12}>
                <Card title={<><LockOutlined style={{ marginRight: 8 }} />Sécurité</>} className="profile-card">
                    <Paragraph type="secondary">
                        Changez votre mot de passe régulièrement pour sécuriser votre compte.
                    </Paragraph>
                    <Button type="primary" icon={<LockOutlined />} onClick={() => setPasswordModalVisible(true)}>
                        Changer le mot de passe
                    </Button>
                </Card>
            </Col>
            </Row>
        </div>
    );

    const renderContent = () => {
        switch (selectedMenu) {
            case 'referrals':
                return renderReferralsContent();
            case 'commissions':
                return renderCommissionsContent();
            case 'revenues-pending':
                return renderPendingRevenuesContent();
            case 'revenues-collected':
                return renderCollectedRevenuesContent();
            case 'revenues-130h':
                return renderRevenues130hContent();
            case 'revenues-bonus-recharge':
                return renderBonusRechargeContent();
            case 'revenues-monthly-retentions':
                return renderMonthlyRetentionsContent();
            case 'driver-progress':
                return renderDriverProgressContent();
            case 'profile':
                return renderProfileContent();
            default:
                return renderDashboardContent();
        }
    };

    return (
        <Layout className="affiliate-dashboard-layout">
            {/* Sidebar */}
            <Sider
                width={240}
                collapsedWidth={80}
                className="dashboard-sider"
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                trigger={null}
                breakpoint="lg"
            >
                {/* Logo */}
                <div className="sider-logo">
                    <img src={logoTigoon} alt="TIGOON Connect" className="sider-logo-img" />
                    {!collapsed && (
                        <div className="sider-logo-text">
                            <span className="logo-title">TIGOON</span>
                            <span className="logo-subtitle">CONNECT</span>
                        </div>
                    )}
                </div>

                {/* Menu */}
                <Menu
                    mode="inline"
                    selectedKeys={[selectedMenu]}
                    onClick={handleMenuClick}
                    className="sider-menu"
                >
                    <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                        Tableau de bord
                    </Menu.Item>
                    <Menu.Item key="referrals" icon={<CarOutlined />}>
                        Mes Chauffeurs
                    </Menu.Item>
                    <SubMenu key="revenues" icon={<WalletOutlined />} title="Revenus">
                        <Menu.Item key="revenues-pending">En cours</Menu.Item>
                        <Menu.Item key="revenues-collected">Encaissés</Menu.Item>
                        <Menu.Item key="revenues-130h">{hoursThreshold}h Mensuel</Menu.Item>
                        <Menu.Item key="revenues-bonus-recharge">Bonus Recharge Jour</Menu.Item>
                        <Menu.Item key="revenues-monthly-retentions">Rétentions Mensuelles</Menu.Item>
                    </SubMenu>
                    {/* Menu Commissions commenté temporairement
                    <Menu.Item key="commissions" icon={<DollarOutlined />}>
                        Mes Commissions
                    </Menu.Item>
                    */}
                </Menu>

                {/* Code Card */}
                {!collapsed && (
                    <div className="sider-code-card">
                        <div className="code-badge">{affiliate?.referralCode}</div>
                        <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={copyCode}
                            className="copy-btn"
                        >
                            Cliquez pour copier
                        </Button>
                    </div>
                )}

                {/* Footer */}
                <div className="sider-footer">
                    <div className="status-indicator"></div>
                    <span>Affilié v1.0</span>
                </div>
            </Sider>

            {/* Main Content */}
            <Layout className="dashboard-main-area" style={{ marginLeft: collapsed ? 80 : 240 }}>
                {/* Topbar */}
                <div className="dashboard-topbar">
                    <div className="topbar-left">
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={() => setCollapsed(!collapsed)}
                            className="collapse-btn"
                        />
                        <div className="page-title">
                            <Title level={4}>{pageInfo.title}</Title>
                            <Text type="secondary">{pageInfo.subtitle}</Text>
                        </div>
                    </div>
                    <div className="topbar-right">
                        <div className="date-badge">
                            <CalendarOutlined />
                            <span>{dayjs().format('dddd D MMMM YYYY')}</span>
                        </div>
{/* Notification - commenté temporairement
                        <Badge count={0}>
                            <Button type="text" icon={<BellOutlined />} className="notification-btn" />
                        </Badge>
*/}
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            open={userDropdownOpen}
                            onOpenChange={setUserDropdownOpen}
                            dropdownRender={() => (
                                <div className="user-dropdown">
                                    <div className="dropdown-header">
                                        <Avatar size={40} icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                        <div>
                                            <Text strong>{affiliate?.firstName} {affiliate?.lastName}</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>Affilié</Text>
                                        </div>
                                    </div>
                                    <div className="dropdown-menu-item" onClick={() => { setSelectedMenu('profile'); setUserDropdownOpen(false); }}>
                                        <UserOutlined />
                                        <span>Mon Profil</span>
                                    </div>
                                    <div className="dropdown-menu-item logout" onClick={() => { setUserDropdownOpen(false); handleLogout(); }}>
                                        <LogoutOutlined />
                                        <span>Se déconnecter</span>
                                    </div>
                                </div>
                            )}
                        >
                            <div className="user-avatar-wrapper">
                                <Text className="user-name">{affiliate?.firstName} {affiliate?.lastName}</Text>
                                <Avatar size={40} style={{ backgroundColor: '#D4A853', cursor: 'pointer' }}>
                                    {affiliate?.firstName?.charAt(0)}{affiliate?.lastName?.charAt(0)}
                                </Avatar>
                            </div>
                        </Dropdown>
                    </div>
                </div>

                {/* Content */}
                <Content className="dashboard-content">
                    {renderContent()}
                </Content>
            </Layout>

            {/* Mobile Header with Hamburger Menu */}
            <div className="mobile-header">
                <Button
                    type="text"
                    icon={<MenuOutlined />}
                    onClick={() => setMobileMenuVisible(true)}
                    className="mobile-hamburger-btn"
                />
                <div className="mobile-header-title">
                    <Title level={5} style={{ margin: 0, color: '#333' }}>{pageInfo.title}</Title>
                </div>
                <Avatar size={36} style={{ backgroundColor: '#D4A853' }}>
                    {affiliate?.firstName?.charAt(0)}{affiliate?.lastName?.charAt(0)}
                </Avatar>
            </div>

            {/* Mobile Drawer Menu */}
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img src={logoTigoon} alt="TIGOON" style={{ height: 32 }} />
                        <span style={{ fontWeight: 600, color: '#333' }}>TIGOON</span>
                    </div>
                }
                placement="left"
                onClose={() => setMobileMenuVisible(false)}
                open={mobileMenuVisible}
                width={280}
                bodyStyle={{ padding: 0 }}
                headerStyle={{ borderBottom: '1px solid #f0f0f0' }}
            >
                <Menu
                    mode="inline"
                    selectedKeys={[selectedMenu]}
                    defaultOpenKeys={['revenues']}
                    style={{ border: 'none' }}
                    onClick={(e) => {
                        setSelectedMenu(e.key);
                        setMobileMenuVisible(false);
                        if (e.key === 'revenues-pending') loadDriversWithProgress();
                        if (e.key === 'revenues-130h') loadRevenues130h();
                        if (e.key === 'revenues-collected') loadCollectedRevenues();
                        if (e.key === 'revenues-bonus-recharge') loadBonusRecharges();
                        if (e.key === 'revenues-monthly-retentions') loadMonthlyRetentions();
                    }}
                >
                    <Menu.Item key="dashboard" icon={<HomeOutlined />}>
                        Tableau de bord
                    </Menu.Item>
                    <Menu.Item key="referrals" icon={<CarOutlined />}>
                        Mes Chauffeurs
                    </Menu.Item>
                    <SubMenu key="revenues" icon={<WalletOutlined />} title="Revenus">
                        <Menu.Item key="revenues-pending">En cours</Menu.Item>
                        <Menu.Item key="revenues-collected">Encaissés</Menu.Item>
                        <Menu.Item key="revenues-130h">{hoursThreshold}h Mensuel</Menu.Item>
                        <Menu.Item key="revenues-bonus-recharge">Bonus Recharge Jour</Menu.Item>
                        <Menu.Item key="revenues-monthly-retentions">Rétentions Mensuelles</Menu.Item>
                    </SubMenu>
                    <Menu.Item key="profile" icon={<UserOutlined />}>
                        Mon Profil
                    </Menu.Item>
                </Menu>

                {/* User info at bottom of drawer */}
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: 16,
                    borderTop: '1px solid #f0f0f0',
                    background: '#fafafa'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <Avatar size={40} style={{ backgroundColor: '#D4A853' }}>
                            {affiliate?.firstName?.charAt(0)}{affiliate?.lastName?.charAt(0)}
                        </Avatar>
                        <div>
                            <Text strong>{affiliate?.firstName} {affiliate?.lastName}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>{affiliate?.phone}</Text>
                        </div>
                    </div>
                    <Button
                        danger
                        block
                        icon={<LogoutOutlined />}
                        onClick={() => { setMobileMenuVisible(false); handleLogout(); }}
                    >
                        Se déconnecter
                    </Button>
                </div>
            </Drawer>

            {/* Mobile Bottom Navigation */}
            <div className="mobile-bottom-nav">
                <div
                    className={`mobile-nav-item ${selectedMenu === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setSelectedMenu('dashboard')}
                >
                    <HomeOutlined />
                    <span>Accueil</span>
                </div>
                <div
                    className={`mobile-nav-item ${selectedMenu === 'referrals' ? 'active' : ''}`}
                    onClick={() => setSelectedMenu('referrals')}
                >
                    <CarOutlined />
                    <span>Chauffeurs</span>
                </div>
                <div
                    className={`mobile-nav-item ${selectedMenu === 'revenues-pending' || selectedMenu === 'revenues-collected' || selectedMenu === 'revenues-130h' || selectedMenu === 'revenues-bonus-recharge' || selectedMenu === 'revenues-monthly-retentions' || selectedMenu === 'driver-progress' ? 'active' : ''}`}
                    onClick={() => { setSelectedMenu('revenues-pending'); loadDriversWithProgress(); }}
                >
                    <WalletOutlined />
                    <span>Revenus</span>
                </div>
                <div
                    className={`mobile-nav-item ${selectedMenu === 'profile' ? 'active' : ''}`}
                    onClick={() => setSelectedMenu('profile')}
                >
                    <UserOutlined />
                    <span>Profil</span>
                </div>
            </div>

            {/* Mobile FAB for adding driver */}
            <Button
                type="primary"
                shape="circle"
                icon={<UserAddOutlined />}
                className="mobile-fab"
                onClick={() => setDriverModalVisible(true)}
            />

            {/* Driver Modal */}
            <Modal
                title="Inscrire un chauffeur"
                open={driverModalVisible}
                onCancel={() => setDriverModalVisible(false)}
                footer={null}
            >
                <Form
                    form={driverForm}
                    layout="vertical"
                    onFinish={handleAddDriver}
                    initialValues={{ countryCode: '+225' }}
                >
                    <Form.Item name="lastName" label="Nom" rules={[{ required: true, message: 'Requis' }]}>
                        <Input
                            prefix={<UserOutlined />}
                            placeholder="Nom"
                            style={{ textTransform: 'uppercase' }}
                            onChange={(e) => driverForm.setFieldValue('lastName', e.target.value.toUpperCase())}
                        />
                    </Form.Item>
                    <Form.Item name="firstName" label="Prénom" rules={[{ required: true, message: 'Requis' }]}>
                        <Input
                            prefix={<UserOutlined />}
                            placeholder="Prénom"
                            style={{ textTransform: 'uppercase' }}
                            onChange={(e) => driverForm.setFieldValue('firstName', e.target.value.toUpperCase())}
                        />
                    </Form.Item>
                    <Form.Item label="Téléphone" required>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Form.Item name="countryCode" noStyle>
                                <Select style={{ width: 100 }}>
                                    <Option value="+225">+225</Option>
                                    <Option value="+223">+223</Option>
                                    <Option value="+226">+226</Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name="phone" noStyle rules={[{ required: true, message: 'Requis' }, { pattern: /^[0-9]{10}$/, message: '10 chiffres' }]}>
                                <Input
                                    prefix={<PhoneOutlined />}
                                    placeholder="0X XX XX XX XX"
                                    maxLength={10}
                                    style={{ flex: 1 }}
                                    onChange={(e) => {
                                        const cleanValue = e.target.value.replace(/\s/g, '');
                                        driverForm.setFieldValue('phone', cleanValue);
                                    }}
                                />
                            </Form.Item>
                        </div>
                    </Form.Item>
                    <Form.Item
                        name="city"
                        label="Ville"
                        rules={[{ required: true, message: 'Veuillez sélectionner une ville' }]}
                    >
                        <Select placeholder="Sélectionner une ville">
                            {cities.map(city => (
                                <Option key={city._id} value={city._id}>
                                    <EnvironmentOutlined style={{ marginRight: 8, color: '#d4a574' }} />
                                    {city.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <div style={{ background: '#f6f6f6', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                        <Text type="secondary">Mot de passe par défaut : <strong>123456</strong></Text>
                    </div>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Button onClick={() => setDriverModalVisible(false)} style={{ marginRight: 8 }}>Annuler</Button>
                        <Button type="primary" htmlType="submit" loading={addingDriver} icon={<UserAddOutlined />}>
                            Créer le compte
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Password Modal */}
            <Modal
                title="Changer le mot de passe"
                open={passwordModalVisible}
                onCancel={() => setPasswordModalVisible(false)}
                footer={null}
            >
                <Form form={passwordForm} layout="vertical" onFinish={handleUpdatePassword}>
                    <Form.Item name="currentPassword" label="Mot de passe actuel" rules={[{ required: true, message: 'Requis' }]}>
                        <Input.Password placeholder="Mot de passe actuel" />
                    </Form.Item>
                    <Form.Item name="newPassword" label="Nouveau mot de passe" rules={[{ required: true, message: 'Requis' }, { min: 6, message: 'Min 6 caractères' }]}>
                        <Input.Password placeholder="Nouveau mot de passe" />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="Confirmer"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Requis' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                    return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Confirmer" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Button onClick={() => setPasswordModalVisible(false)} style={{ marginRight: 8 }}>Annuler</Button>
                        <Button type="primary" htmlType="submit" loading={updating}>Changer</Button>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default AffiliateDashboardPage;
